import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog as UIDialog, DialogContent as UIDialogContent, DialogHeader as UIDialogHeader, DialogTitle as UIDialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { db, auth, storage } from "@/lib/firebase";
import { collection, getDocs, query, where, addDoc, onSnapshot, doc, getDoc, Timestamp, updateDoc, orderBy } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UserPlus, Users, GraduationCap, Search, Clock, CheckCircle, CalendarClock, Upload, FileText, CreditCard, Award, ArrowRightLeft, Edit2 } from "lucide-react";
import MessageDialog from "@/components/ui/MessageDialog";
import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";

interface Student {
  id?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  phone: string;
  email?: string;
  address?: string;
  dateOfBirth?: string;
  gender?: string;
  branch: string;
  registrationType?: "online" | "walk-in";
  createdAt: any;
  createdBy?: string;
  role: string;
  uid?: string;
  ltoClientNumber?: string;
  maritalStatus?: string;
  licenseNumber?: string;
}

interface Enrollment {
  id: string;
  studentId: string;
  courseId: string;
  branch: string;
  courseFee: number;
  amountReceived: number;
  balance: number;
  status: "pending" | "approved" | "rejected" | "partial_payment" | "completed";
  enrolledAt: any;
  paymentMethod: string;
  paymentReceived: boolean;
  notes?: string;
  registrationType: "online" | "walk-in";
  enrolledBy?: string;
  licenseCode?: string; // Add license code field
  licenseCodes?: string[]; // For multiple license codes
  licenseCodeIds?: string[]; // For license code IDs
  licenseCodeTotalPrice?: number; // Total price of assigned license codes
  paymentReceiptUrl?: string; // For uploaded payment receipts
  certificateUrl?: string; // For uploaded certificates
  rejectionReason?: string; // For payment rejection reasons
  orNumber?: string; // Official Receipt number (legacy - for backward compatibility)
  orNotes?: string; // OR notes (legacy - for backward compatibility)
  orNumbers?: string[]; // Array of OR numbers for multiple payments
  orEntries?: Array<{
    orNumber: string;
    notes: string;
    amount: number;
    addedAt: any;
    addedBy: string;
  }>; // Detailed OR entries
  // Discount fields
  discountAmount?: number;
  discountType?: string;
  discountReason?: string;
  discountApplied?: boolean;
  discountValue?: number;
  discountNotes?: string;
  discountAppliedAt?: any;
  discountAppliedBy?: string;
}

interface LicenseCode {
  id: string;
  code: string;
  description: string;
  price: number;
  isActive: boolean;
}

interface Schedule {
  id: string;
  studentId: string;
  enrollmentId: string;
  courseId: string;
  timeSlotId: string;
  instructorId: string;
  date: string;
  time: string;
  status: "scheduled" | "completed" | "cancelled";
  scheduledAt: any;
  scheduledBy?: string;
  notes?: string;
  registrationType: "online" | "walk-in";
  branch: string;
}

const StaffEnrollmentManagement: React.FC = () => {
  // State for all students (both online and walk-in)
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [licenseCodes, setLicenseCodes] = useState<LicenseCode[]>([]);
  const [studentPreferences, setStudentPreferences] = useState<any[]>([]);
  const [orBatches, setOrBatches] = useState<any[]>([]);
  const [userBranch, setUserBranch] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"online" | "walk-in">("online");
  
  // Filter states
  // const [selectedStatus, setSelectedStatus] = useState<string>("all"); // REMOVED - causing display issues
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showSessionsDialog, setShowSessionsDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showLicenseCodeDialog, setShowLicenseCodeDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showLicenseAssignDialog, setShowLicenseAssignDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showQuickCompleteDialog, setShowQuickCompleteDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [editFormData, setEditFormData] = useState({
    ltoClientNumber: '',
    licenseNumber: '',
    mobileNumber: '',
    address: ''
  });
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  
  // Form states
  const [newStudent, setNewStudent] = useState<Partial<Student>>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    dateOfBirth: "",
    gender: "",
  });
  
  const [enrollmentData, setEnrollmentData] = useState({
    courseId: "",
    paymentMethod: "cash",
    paymentAmount: 0,
    notes: "",
    orNumber: "",
    orNotes: "",
    // Discount fields
    discountApplied: false,
    discountType: "",
    discountAmount: 0,
    discountReason: "",
  });
  
  const [scheduleData, setScheduleData] = useState({
    timeSlotId: "",
    date: "",
    instructorId: "",
    notes: "",
  });
  
  const [rescheduleData, setRescheduleData] = useState({
    timeSlotId: "",
    date: "",
    instructorId: "",
    notes: "",
  });
  
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paymentMethod: "cash",
    notes: "",
    orNumber: "",
    orNotes: "",
  });
  
  const [licenseCodeData, setLicenseCodeData] = useState({
    licenseCodeId: "",
    notes: "",
  });
  
  // Certificate upload and license code assignment states
  const [selectedCertificateFile, setSelectedCertificateFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [allLicenseCodes, setAllLicenseCodes] = useState<any[]>([]);
  const [selectedLicenseCodes, setSelectedLicenseCodes] = useState<string[]>([]);
  
  // Transfer student data
  const [transferData, setTransferData] = useState({
    newBranch: ""
  });
  
  // Message dialog
  const [messageDialog, setMessageDialog] = useState({
    show: false,
    title: "",
    message: "",
    type: "info" as "info" | "success" | "error" | "warning",
  });

  // Loading states
  const [creating, setCreating] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [assigningLicenseCode, setAssigningLicenseCode] = useState(false);
  const [completingSession, setCompletingSession] = useState<string | null>(null);
  const [completingEnrollment, setCompletingEnrollment] = useState(false);
  const [selectedCourseForCompletion, setSelectedCourseForCompletion] = useState<string>("");
  const [approvingPayment, setApprovingPayment] = useState(false);
  const [rejectingPayment, setRejectingPayment] = useState(false);

  // Payment review data
  const [paymentReviewData, setPaymentReviewData] = useState({
    rejectionReason: "",
  });

  // Message state helper
  const [message, setMessage] = useState({
    open: false,
    text: "",
    type: "info" as "info" | "success" | "error",
  });

  useEffect(() => {
    const setupData = async () => {
      if (!auth.currentUser) return;
      
      try {
        // Get staff user's branch
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        const userData = userDoc.data();
        const branch = userData?.branch || "";
        setUserBranch(branch);

        // Set up real-time listeners
        const unsubscribers: (() => void)[] = [];

        // Listen to all students for this branch
        const studentsQuery = query(
          collection(db, "users"), 
          where("role", "==", "student"),
          where("branch", "==", branch),
          orderBy("createdAt", "desc")
        );
        const studentsUnsubscribe = onSnapshot(studentsQuery, (snapshot) => {
          const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Student[];
          setAllStudents(students);
        });
        unsubscribers.push(studentsUnsubscribe);

        // Listen to courses
        const coursesUnsubscribe = onSnapshot(collection(db, "courses"), (snapshot) => {
          setCourses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(coursesUnsubscribe);

        // Listen to branches
        const branchesUnsubscribe = onSnapshot(collection(db, "branches"), (snapshot) => {
          setBranches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(branchesUnsubscribe);

        // Listen to time slots for this branch
        const timeSlotsQuery = query(
          collection(db, "timeSlots"), 
          where("branch", "==", branch)
        );
        const timeSlotsUnsubscribe = onSnapshot(timeSlotsQuery, (snapshot) => {
          setTimeSlots(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(timeSlotsUnsubscribe);

        // Listen to instructors
        const instructorsQuery = query(
          collection(db, "users"), 
          where("role", "==", "instructor")
        );
        const instructorsUnsubscribe = onSnapshot(instructorsQuery, (snapshot) => {
          setInstructors(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(instructorsUnsubscribe);

        // Listen to schedules for this branch
        const schedulesQuery = query(
          collection(db, "schedules"), 
          where("branch", "==", branch)
        );
        const schedulesUnsubscribe = onSnapshot(schedulesQuery, (snapshot) => {
          setSchedules(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Schedule[]);
        });
        unsubscribers.push(schedulesUnsubscribe);

        // Listen to license codes
        const licenseCodesUnsubscribe = onSnapshot(collection(db, "licenseCodes"), (snapshot) => {
          const licenseCodesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as LicenseCode[];
          setLicenseCodes(licenseCodesData);
          setAllLicenseCodes(licenseCodesData); // Also set for assignment dialog
        });
        unsubscribers.push(licenseCodesUnsubscribe);

        // Listen to OR batches for this branch
        const orBatchesQuery = query(
          collection(db, "or"),
          where("branch", "==", branch)
        );
        const orBatchesUnsubscribe = onSnapshot(orBatchesQuery, (snapshot) => {
          setOrBatches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(orBatchesUnsubscribe);

        // Listen to student preferences
        const studentPreferencesUnsubscribe = onSnapshot(collection(db, "studentPreferences"), (snapshot) => {
          setStudentPreferences(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(studentPreferencesUnsubscribe);

        setLoading(false);

        // Cleanup function
        return () => {
          unsubscribers.forEach(unsubscribe => unsubscribe());
        };
      } catch (error) {
        console.error("Error setting up enrollment management data:", error);
        setMessage({ open: true, text: "Failed to load data.", type: "error" });
        setLoading(false);
      }
    };

    setupData();
  }, []);

  // Separate useEffect for filtered enrollments to minimize reads
  useEffect(() => {
    if (!userBranch) return;

    const setupFilteredEnrollments = async () => {
      try {
        // Simple query for all enrollments without status filtering
        const enrollmentsQuery = query(
          collection(db, "enrollments"),
          where("branch", "==", userBranch),
          orderBy("enrolledAt", "desc")
        );

        // Set up real-time listener for filtered enrollments
        const unsubscribe = onSnapshot(enrollmentsQuery, (snapshot) => {
          setEnrollments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Enrollment[]);
        });

        return unsubscribe;
      } catch (error) {
        console.error("Error setting up filtered enrollments:", error);
        setMessage({ open: true, text: "Failed to load filtered enrollments.", type: "error" });
        return () => {}; // Return empty function on error
      }
    };

    let unsubscribe: (() => void) | undefined;
    
    setupFilteredEnrollments().then(unsub => {
      unsubscribe = unsub;
    });
    
    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userBranch]); // Removed selectedStatus dependency

  const handleEdit = (student: Student) => {
    setSelectedStudent(student);
    setEditFormData({
      ltoClientNumber: student.ltoClientNumber || '',
      licenseNumber: student.licenseNumber || '',
      mobileNumber: student.phone || '',
      address: student.address || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateStudent = async () => {
    if (!selectedStudent) return;
    
    try {
      const studentRef = doc(db, 'users', selectedStudent.id);
      await updateDoc(studentRef, {
        ltoClientNumber: editFormData.ltoClientNumber,
        licenseNumber: editFormData.licenseNumber,
        phone: editFormData.mobileNumber,
        address: editFormData.address,
        updatedAt: new Date()
      });
      
      // Update local state
      setAllStudents(prev => 
        prev.map(student => 
          student.id === selectedStudent.id 
            ? { 
                ...student, 
                ltoClientNumber: editFormData.ltoClientNumber,
                licenseNumber: editFormData.licenseNumber,
                phone: editFormData.mobileNumber,
                address: editFormData.address
              }
            : student
        )
      );
      
      setShowEditDialog(false);
      setSelectedStudent(null);
      setMessage({ open: true, text: 'Student information updated successfully!', type: 'success' });
    } catch (error) {
      console.error('Error updating student:', error);
      setMessage({ open: true, text: 'Error updating student information', type: 'error' });
    }
  };

  const handleViewDetails = (student: Student) => {
    setSelectedStudent(student);
    setShowDetailsDialog(true);
  };

  const handleCreateStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newStudent.firstName?.trim() || !newStudent.lastName?.trim() || !newStudent.phone?.trim()) {
      setMessage({ open: true, text: "First name, last name, and phone are required.", type: "error" });
      return;
    }

    setCreating(true);
    try {
      // Create unique student ID for walk-in students
      const studentId = `walkin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const studentData = {
        ...newStudent,
        branch: userBranch,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser!.uid,
        role: "student",
        uid: studentId, // Custom UID for walk-in students
      };

      await addDoc(collection(db, "users"), studentData);

      setMessage({ open: true, text: "Walk-in student created successfully!", type: "success" });
      setShowCreateDialog(false);
      setNewStudent({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        address: "",
        dateOfBirth: "",
        gender: "",
      });
    } catch (error) {
      console.error("Error creating walk-in student:", error);
      setMessage({ open: true, text: "Failed to create student.", type: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleEnrollStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!enrollmentData.courseId || !selectedStudent) {
      setMessage({ open: true, text: "Please select a course.", type: "error" });
      return;
    }

    // Check if student is already enrolled in this course
    const existingEnrollment = enrollments.find(
      e => e.studentId === selectedStudent.id && e.courseId === enrollmentData.courseId
    );
    
    if (existingEnrollment) {
      setMessage({ open: true, text: "Student is already enrolled in this course.", type: "info" });
      return;
    }

    setEnrolling(true);
    try {
      const selectedCourse = courses.find(c => c.id === enrollmentData.courseId);
      const courseFee = selectedCourse?.price || 0;
      const discountAmount = enrollmentData.discountAmount || 0;
      const netFee = courseFee - discountAmount;
      const amountReceived = enrollmentData.paymentAmount || 0;
      const balance = netFee - amountReceived;

      // Determine registration type: walk-in students have UIDs starting with "walkin_"
      const registrationType = selectedStudent.uid?.startsWith("walkin_") ? "walk-in" : "online";

      const enrollmentDoc = {
        studentId: selectedStudent.id,
        courseId: enrollmentData.courseId,
        branch: userBranch,
        courseFee,
        discountAmount,
        netFee,
        amountReceived,
        balance,
        status: balance <= 0 ? "approved" : "partial_payment", // Approved if fully paid, partial if balance remains
        enrolledAt: Timestamp.now(),
        paymentMethod: enrollmentData.paymentMethod,
        paymentReceived: amountReceived > 0,
        notes: enrollmentData.notes,
        registrationType,
        enrolledBy: auth.currentUser!.uid,
        // Add OR number if provided and payment is made
        orNumber: (amountReceived > 0 && enrollmentData.orNumber?.trim()) ? enrollmentData.orNumber.trim() : "",
        orNotes: (amountReceived > 0 && enrollmentData.orNotes?.trim()) ? enrollmentData.orNotes.trim() : "",
      };

      // Validate OR number if provided
      if (enrollmentDoc.orNumber) {
        const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
        if (!availableNumbers.includes(enrollmentDoc.orNumber)) {
          setMessage({ open: true, text: "Invalid OR number. Please select a valid OR number.", type: "error" });
          return;
        }
      }

      await addDoc(collection(db, "enrollments"), enrollmentDoc);

      // Mark OR number as used in the OR batch if provided
      if (enrollmentDoc.orNumber) {
        const orBatch = orBatches.find(batch => 
          batch.availableNumbers.includes(enrollmentDoc.orNumber)
        );
        
        if (orBatch) {
          const batchRef = doc(db, "or", orBatch.id);
          const updatedUsedNumbers = [...orBatch.usedNumbers, enrollmentDoc.orNumber];
          const updatedAvailableNumbers = orBatch.availableNumbers.filter(
            (num: string) => num !== enrollmentDoc.orNumber
          );
          
          await updateDoc(batchRef, {
            usedNumbers: updatedUsedNumbers,
            availableNumbers: updatedAvailableNumbers
          });
        }
      }

      setMessage({ open: true, text: "Student enrolled successfully!", type: "success" });
      setShowEnrollDialog(false);
      setSelectedStudent(null);
      setEnrollmentData({
        courseId: "",
        paymentMethod: "cash",
        paymentAmount: 0,
        notes: "",
        orNumber: "",
        orNotes: "",
        // Discount fields
        discountApplied: false,
        discountType: "",
        discountAmount: 0,
        discountReason: "",
      });
    } catch (error) {
      console.error("Error enrolling student:", error);
      setMessage({ open: true, text: "Failed to enroll student.", type: "error" });
    } finally {
      setEnrolling(false);
    }
  };

  const handleScheduleStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!scheduleData.timeSlotId || !scheduleData.date || !scheduleData.instructorId || !selectedEnrollment) {
      setMessage({ open: true, text: "Please fill in all required fields.", type: "error" });
      return;
    }

    // Check if slot has reached maximum capacity
    const schedulesForSlot = schedules.filter(schedule => 
      schedule.timeSlotId === scheduleData.timeSlotId && 
      schedule.date === scheduleData.date
    );
    
    const timeSlot = timeSlots.find(ts => ts.id === scheduleData.timeSlotId);
    const maxStudents = timeSlot?.maxStudents || 1;
    
    if (schedulesForSlot.length >= maxStudents) {
      setMessage({ open: true, text: "This time slot is at maximum capacity. Please select a different time slot.", type: "error" });
      return;
    }

    setScheduling(true);
    
    // Determine registration type safely - fallback for enrollments without registrationType
    let registrationType = selectedEnrollment.registrationType;
    if (!registrationType) {
      // Fallback: detect by payment receipt or student type
      if (selectedEnrollment.paymentReceiptUrl || (selectedEnrollment as any).paymentUploaded === true) {
        registrationType = "online";
      } else {
        // Get student data to check if it's a walk-in student
        const student = allStudents.find(s => s.id === selectedEnrollment.studentId);
        registrationType = student?.uid?.startsWith("walkin_") ? "walk-in" : "online";
      }
    }
    
    try {
      await addDoc(collection(db, "schedules"), {
        studentId: selectedEnrollment.studentId,
        enrollmentId: selectedEnrollment.id,
        courseId: selectedEnrollment.courseId,
        timeSlotId: scheduleData.timeSlotId,
        instructorId: scheduleData.instructorId,
        date: scheduleData.date,
        time: `${timeSlot?.startTime} - ${timeSlot?.endTime}`,
        status: "scheduled",
        scheduledAt: Timestamp.now(),
        scheduledBy: auth.currentUser!.uid, // Track which staff member scheduled
        notes: scheduleData.notes,
        registrationType: registrationType,
        branch: userBranch,
      });

      setMessage({ open: true, text: "Session scheduled successfully!", type: "success" });
      setShowScheduleDialog(false);
      setSelectedEnrollment(null);
      setScheduleData({
        timeSlotId: "",
        date: "",
        instructorId: "",
        notes: "",
      });
    } catch (error) {
      console.error("Error scheduling session:", error);
      setMessage({ open: true, text: "Failed to schedule session.", type: "error" });
    } finally {
      setScheduling(false);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEnrollment || paymentData.amount <= 0) {
      setMessage({ open: true, text: "Please enter a valid payment amount.", type: "error" });
      return;
    }

    // If OR number is provided, validate it
    if (paymentData.orNumber.trim()) {
      const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
      if (!availableNumbers.includes(paymentData.orNumber.trim())) {
        setMessage({ open: true, text: `OR number "${paymentData.orNumber.trim()}" is not available or does not exist.`, type: "error" });
        return;
      }
    }

    setProcessingPayment(true);
    try {
      const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
      const newAmountReceived = selectedEnrollment.amountReceived + paymentData.amount;
      
      // Calculate balance correctly considering any discount applied
      const courseFee = selectedEnrollment.courseFee || 0;
      const discountAmount = selectedEnrollment.discountAmount || 0;
      const amountDue = courseFee - discountAmount; // What student actually needs to pay
      const newBalance = Math.max(0, amountDue - newAmountReceived);
      const newStatus = newBalance <= 0 ? "approved" : "partial_payment";

      // Prepare update data
      const updateData: any = {
        amountReceived: newAmountReceived,
        balance: newBalance,
        status: newStatus,
        lastPaymentAt: Timestamp.now(),
        lastPaymentAmount: paymentData.amount,
        lastPaymentMethod: paymentData.paymentMethod,
        lastPaymentNotes: paymentData.notes,
      };

      // Handle OR number if provided
      if (paymentData.orNumber.trim()) {
        const currentORNumbers = selectedEnrollment.orNumbers || [];
        const currentOREntries = selectedEnrollment.orEntries || [];
        
        // Add new OR number to arrays
        updateData.orNumbers = [...currentORNumbers, paymentData.orNumber.trim()];
        updateData.orEntries = [...currentOREntries, {
          orNumber: paymentData.orNumber.trim(),
          notes: paymentData.orNotes.trim(),
          amount: paymentData.amount,
          addedAt: Timestamp.now(),
          addedBy: auth.currentUser!.uid
        }];

        // Mark OR number as used in the OR batch
        const orBatch = orBatches.find(batch => 
          batch.availableNumbers.includes(paymentData.orNumber.trim())
        );
        
        if (orBatch) {
          const batchRef = doc(db, "or", orBatch.id);
          const updatedUsedNumbers = [...orBatch.usedNumbers, paymentData.orNumber.trim()];
          const updatedAvailableNumbers = orBatch.availableNumbers.filter(
            (num: string) => num !== paymentData.orNumber.trim()
          );
          
          await updateDoc(batchRef, {
            usedNumbers: updatedUsedNumbers,
            availableNumbers: updatedAvailableNumbers
          });
        }
      }

      await updateDoc(enrollmentRef, updateData);

      setMessage({ open: true, text: "Payment recorded successfully!", type: "success" });
      setShowPaymentDialog(false);
      setSelectedEnrollment(null);
      setPaymentData({
        amount: 0,
        paymentMethod: "cash",
        notes: "",
        orNumber: "",
        orNotes: "",
      });
    } catch (error) {
      console.error("Error recording payment:", error);
      setMessage({ open: true, text: "Failed to record payment.", type: "error" });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleAssignLicenseCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEnrollment || !licenseCodeData.licenseCodeId) {
      setMessage({ open: true, text: "Please select a license code.", type: "error" });
      return;
    }

    setAssigningLicenseCode(true);
    try {
      const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
      const selectedLicenseCode = licenseCodes.find(lc => lc.id === licenseCodeData.licenseCodeId);

      await updateDoc(enrollmentRef, {
        licenseCode: selectedLicenseCode?.code,
        licenseCodeId: licenseCodeData.licenseCodeId,
        licenseCodeAssignedAt: Timestamp.now(),
        licenseCodeAssignedBy: auth.currentUser!.uid,
        licenseCodeNotes: licenseCodeData.notes,
      });

      setMessage({ open: true, text: "License code assigned successfully!", type: "success" });
      setShowLicenseCodeDialog(false);
      setSelectedEnrollment(null);
      setLicenseCodeData({
        licenseCodeId: "",
        notes: "",
      });
    } catch (error) {
      console.error("Error assigning license code:", error);
      setMessage({ open: true, text: "Failed to assign license code.", type: "error" });
    } finally {
      setAssigningLicenseCode(false);
    }
  };

  // Certificate Upload Handler
  const handleUploadCertificate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEnrollment || !selectedCertificateFile) {
      setMessage({ open: true, text: "Please select a file to upload.", type: "error" });
      return;
    }

    setUploading(true);
    try {
      // Upload file to Firebase Storage
      const storageRef = ref(storage, `certificates/${selectedEnrollment.id}_${Date.now()}_${selectedCertificateFile.name}`);
      const uploadResult = await uploadBytes(storageRef, selectedCertificateFile);
      const url = await getDownloadURL(uploadResult.ref);

      // Update enrollment with certificate URL
      const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
      await updateDoc(enrollmentRef, {
        certificateUrl: url,
        certificateUploadedAt: Timestamp.now(),
        certificateUploadedBy: auth.currentUser!.uid,
      });

      setMessage({ open: true, text: "Certificate uploaded successfully!", type: "success" });
      setShowUploadDialog(false);
      setSelectedEnrollment(null);
      setSelectedCertificateFile(null);
    } catch (error) {
      console.error("Error uploading certificate:", error);
      setMessage({ open: true, text: "Failed to upload certificate.", type: "error" });
    } finally {
      setUploading(false);
    }
  };

  // OR Number Assignment Handler
  // License Code Assignment Handler (Multiple Selection)
  const handleAssignMultipleLicenseCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEnrollment || selectedLicenseCodes.length === 0) {
      setMessage({ open: true, text: "Please select at least one license code.", type: "error" });
      return;
    }

    setAssigningLicenseCode(true);
    try {
      // Get license code details
      const selectedCodeDetails = licenseCodes.filter(lc => selectedLicenseCodes.includes(lc.id));
      const codeStrings = selectedCodeDetails.map(lc => lc.code);

      // Calculate total price of newly selected license codes
      const newLicenseCodesPrice = selectedCodeDetails.reduce((total, lc) => total + (Number(lc.price) || 0), 0);

      // Get existing license codes or initialize empty array
      const currentLicenseCodes = selectedEnrollment.licenseCodes || [];
      const currentLicenseCodeIds = selectedEnrollment.licenseCodeIds || [];
      const currentLicenseCodePrice = Number(selectedEnrollment.licenseCodeTotalPrice) || 0;

      // Only add new license codes (avoid duplicates)
      const newLicenseCodeIds = selectedLicenseCodes.filter(id => !currentLicenseCodeIds.includes(id));
      const newLicenseCodeStrings = licenseCodes
        .filter(lc => newLicenseCodeIds.includes(lc.id))
        .map(lc => lc.code);

      // Calculate price only for new license codes
      const additionalPrice = licenseCodes
        .filter(lc => newLicenseCodeIds.includes(lc.id))
        .reduce((total, lc) => total + (Number(lc.price) || 0), 0);

      const updatedLicenseCodeIds = [...currentLicenseCodeIds, ...newLicenseCodeIds];
      const updatedLicenseCodeStrings = [...currentLicenseCodes, ...newLicenseCodeStrings];
      const updatedTotalPrice = currentLicenseCodePrice + additionalPrice;

      const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
      await updateDoc(enrollmentRef, {
        licenseCodes: updatedLicenseCodeStrings,
        licenseCodeIds: updatedLicenseCodeIds,
        licenseCodeTotalPrice: updatedTotalPrice,
        licenseCodesAssignedAt: Timestamp.now(),
        licenseCodesAssignedBy: auth.currentUser!.uid,
      });

      const assignedCount = newLicenseCodeIds.length;
      if (assignedCount > 0) {
        setMessage({ 
          open: true, 
          text: `${assignedCount} license code(s) assigned successfully! Total cost: ₱${additionalPrice.toLocaleString()}`, 
          type: "success" 
        });
      } else {
        setMessage({ 
          open: true, 
          text: "No new license codes were assigned (duplicates skipped).", 
          type: "info" 
        });
      }
      
      setShowLicenseAssignDialog(false);
      setSelectedEnrollment(null);
      setSelectedLicenseCodes([]);
    } catch (error) {
      console.error("Error assigning license codes:", error);
      setMessage({ open: true, text: "Failed to assign license codes.", type: "error" });
    } finally {
      setAssigningLicenseCode(false);
    }
  };

  // Discount Application Handler
  const handleCompleteSession = async (scheduleId: string, sessionHours: number) => {
    if (!window.confirm("Are you sure you want to mark this session as complete? This action cannot be undone.")) {
      return;
    }

    setCompletingSession(scheduleId);
    try {
      // Find the schedule to get enrollment info
      const schedule = schedules.find(s => s.id === scheduleId);
      if (!schedule) {
        throw new Error("Schedule not found");
      }

      // Update the session as completed
      const scheduleRef = doc(db, "schedules", scheduleId);
      await updateDoc(scheduleRef, {
        status: "completed",
        completedAt: Timestamp.now(),
        completedBy: auth.currentUser!.uid,
        sessionHours: sessionHours
      });

      // Check if the course is now fully completed
      const enrollment = enrollments.find(e => e.id === schedule.enrollmentId);
      if (enrollment) {
        // Calculate progress after this session completion
        const course = courses.find(c => c.id === enrollment.courseId);
        if (course && course.totalHours) {
          const completedSessions = schedules.filter(
            s => s.enrollmentId === enrollment.id && 
            (s.status === "completed" || s.id === scheduleId) // Include the current session being completed
          );
          
          const completedHours = completedSessions.reduce((total, session) => {
            const timeSlot = timeSlots.find(ts => ts.id === session.timeSlotId);
            if (timeSlot && timeSlot.startTime && timeSlot.endTime) {
              const [startHour, startMin] = timeSlot.startTime.split(':').map(Number);
              const [endHour, endMin] = timeSlot.endTime.split(':').map(Number);
              const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
              return total + (duration / 60);
            }
            return total + 4; // fallback to 4 hours if time slot not found
          }, 0);

          // If course is completed (>=100%), update enrollment status
          if (completedHours >= course.totalHours && enrollment.status !== "completed") {
            // Check if there's still an outstanding balance
            const balance = Number(enrollment.balance) || 0;
            if (balance > 0) {
              setMessage({ 
                open: true, 
                text: `Course cannot be marked as completed. Outstanding balance: ₱${balance.toLocaleString()}. Please collect payment first.`, 
                type: "error" 
              });
              return;
            }
            
            const enrollmentRef = doc(db, "enrollments", enrollment.id);
            await updateDoc(enrollmentRef, {
              status: "completed",
              completedAt: Timestamp.now(),
              completedBy: auth.currentUser!.uid
            });
            setMessage({ open: true, text: "Session completed! Course has been marked as completed.", type: "success" });
          } else {
            setMessage({ open: true, text: "Session marked as complete!", type: "success" });
          }
        } else {
          setMessage({ open: true, text: "Session marked as complete!", type: "success" });
        }
      } else {
        setMessage({ open: true, text: "Session marked as complete!", type: "success" });
      }
    } catch (error) {
      console.error("Error completing session:", error);
      setMessage({ open: true, text: "Failed to complete session.", type: "error" });
    } finally {
      setCompletingSession(null);
    }
  };

  const handleMarkEnrollmentComplete = async (enrollmentId: string) => {
    setCompletingEnrollment(true);
    try {
      // Get the enrollment to check balance
      const enrollmentDoc = await getDoc(doc(db, "enrollments", enrollmentId));
      const enrollmentData = enrollmentDoc.data();
      
      if (enrollmentData) {
        const balance = Number(enrollmentData.balance) || 0;
        if (balance > 0) {
          setMessage({ 
            open: true, 
            text: `Course cannot be marked as completed. Outstanding balance: ₱${balance.toLocaleString()}. Please collect payment first.`, 
            type: "error" 
          });
          setCompletingEnrollment(false);
          return;
        }
      }
      
      const enrollmentRef = doc(db, "enrollments", enrollmentId);
      await updateDoc(enrollmentRef, {
        status: "completed",
        completedAt: Timestamp.now(),
        completedBy: auth.currentUser!.uid
      });
      setMessage({ open: true, text: "Course marked as completed!", type: "success" });
      setShowCompletionDialog(false);
      setSelectedEnrollment(null);
    } catch (error) {
      console.error("Error marking enrollment as complete:", error);
      setMessage({ open: true, text: "Failed to mark course as complete.", type: "error" });
    } finally {
      setCompletingEnrollment(false);
    }
  };

  const handleQuickComplete = async () => {
    if (!selectedCourseForCompletion || !selectedStudent) return;
    
    setCompletingEnrollment(true);
    try {
      // Get the enrollment to check balance
      const enrollmentDoc = await getDoc(doc(db, "enrollments", selectedCourseForCompletion));
      const enrollmentData = enrollmentDoc.data();
      
      if (enrollmentData) {
        const balance = Number(enrollmentData.balance) || 0;
        if (balance > 0) {
          setMessage({ 
            open: true, 
            text: `Course cannot be marked as completed. Outstanding balance: ₱${balance.toLocaleString()}. Please collect payment first.`, 
            type: "error" 
          });
          setCompletingEnrollment(false);
          return;
        }
      }
      
      await updateDoc(doc(db, "enrollments", selectedCourseForCompletion), {
        status: "completed",
        completedAt: Timestamp.now(),
        completedBy: auth.currentUser!.uid
      });
      setMessage({ open: true, text: "Course marked as completed!", type: "success" });
      setShowQuickCompleteDialog(false);
      setSelectedCourseForCompletion("");
      setSelectedStudent(null);
    } catch (error) {
      console.error("Error marking enrollment as complete:", error);
      setMessage({ open: true, text: "Failed to mark course as complete.", type: "error" });
    } finally {
      setCompletingEnrollment(false);
    }
  };

  const handleRescheduleSession = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rescheduleData.timeSlotId || !rescheduleData.date || !rescheduleData.instructorId || !selectedSchedule) {
      setMessage({ open: true, text: "Please fill in all required fields.", type: "error" });
      return;
    }

    // Check if new slot has reached maximum capacity
    const schedulesForSlot = schedules.filter(schedule => 
      schedule.timeSlotId === rescheduleData.timeSlotId && 
      schedule.date === rescheduleData.date &&
      schedule.id !== selectedSchedule.id // Exclude current schedule
    );
    
    const timeSlot = timeSlots.find(ts => ts.id === rescheduleData.timeSlotId);
    const maxStudents = timeSlot?.maxStudents || 1;
    
    if (schedulesForSlot.length >= maxStudents) {
      setMessage({ open: true, text: "This time slot is at maximum capacity. Please select a different time slot.", type: "error" });
      return;
    }

    setRescheduling(true);
    
    try {
      const scheduleRef = doc(db, "schedules", selectedSchedule.id);
      await updateDoc(scheduleRef, {
        timeSlotId: rescheduleData.timeSlotId,
        instructorId: rescheduleData.instructorId,
        date: rescheduleData.date,
        time: `${timeSlot?.startTime} - ${timeSlot?.endTime}`,
        notes: rescheduleData.notes,
        rescheduledAt: Timestamp.now(),
        rescheduledBy: auth.currentUser!.uid,
        originalDate: selectedSchedule.date,
        originalTime: selectedSchedule.time,
      });

      setMessage({ open: true, text: "Session rescheduled successfully!", type: "success" });
      setShowRescheduleDialog(false);
      setSelectedSchedule(null);
      setRescheduleData({
        timeSlotId: "",
        date: "",
        instructorId: "",
        notes: "",
      });
    } catch (error) {
      console.error("Error rescheduling session:", error);
      setMessage({ open: true, text: "Failed to reschedule session.", type: "error" });
    } finally {
      setRescheduling(false);
    }
  };

  // Payment review handlers
  const handleApprovePayment = async (enrollmentId: string) => {
    setApprovingPayment(true);
    try {
      const enrollmentRef = doc(db, "enrollments", enrollmentId);
      const enrollmentDoc = await getDoc(enrollmentRef);
      const enrollmentData = enrollmentDoc.data();
      
      // When approving payment, set amountReceived to courseFee and calculate balance
      const courseFee = enrollmentData?.courseFee || 0;
      const newBalance = 0; // Full payment means no balance
      
      await updateDoc(enrollmentRef, { 
        status: "approved",
        amountReceived: courseFee,
        balance: newBalance,
        paymentReceived: true,
        approvedAt: Timestamp.now()
      });

      // Create notification for student
      await addDoc(collection(db, "notifications"), {
        userId: enrollmentData?.studentId,
        type: "payment_approved",
        title: "Payment Approved",
        message: "Your payment has been verified and approved. You can now submit schedule preferences.",
        read: false,
        createdAt: Timestamp.now()
      });
      
      setMessage({ open: true, text: "Payment approved successfully!", type: "success" });
    } catch (error) {
      console.error("Error approving payment:", error);
      setMessage({ open: true, text: "Failed to approve payment.", type: "error" });
    } finally {
      setApprovingPayment(false);
    }
  };

  const handleRejectPayment = async (enrollmentId: string, rejectionReason: string) => {
    if (!rejectionReason.trim()) {
      setMessage({ open: true, text: "Please provide a reason for rejection.", type: "error" });
      return;
    }
    
    setRejectingPayment(true);
    try {
      const enrollmentRef = doc(db, "enrollments", enrollmentId);
      const enrollmentDoc = await getDoc(enrollmentRef);
      const enrollmentData = enrollmentDoc.data();
      
      await updateDoc(enrollmentRef, { 
        status: "rejected",
        rejectionReason: rejectionReason.trim(),
        rejectedAt: Timestamp.now()
      });

      // Create notification for student
      await addDoc(collection(db, "notifications"), {
        userId: enrollmentData?.studentId,
        type: "payment_rejected",
        title: "Payment Rejected",
        message: `Your payment has been rejected. Reason: ${rejectionReason.trim()}`,
        read: false,
        createdAt: Timestamp.now()
      });

      setMessage({ open: true, text: "Payment rejected successfully!", type: "success" });
      setPaymentReviewData({ rejectionReason: "" });
    } catch (error) {
      console.error("Error rejecting payment:", error);
      setMessage({ open: true, text: "Failed to reject payment.", type: "error" });
    } finally {
      setRejectingPayment(false);
    }
  };

  // Transfer student handler
  const handleTransferStudent = async (studentId: string, newBranch: string) => {
    try {
      // Update student's branch in users collection
      const userRef = doc(db, "users", studentId);
      await updateDoc(userRef, { 
        branch: newBranch,
        transferredAt: Timestamp.now(),
        transferredBy: auth.currentUser?.uid
      });

      // Update all enrollments for this student to new branch
      const studentEnrollments = enrollments.filter(e => e.studentId === studentId);
      for (const enrollment of studentEnrollments) {
        const enrollmentRef = doc(db, "enrollments", enrollment.id);
        await updateDoc(enrollmentRef, { 
          branch: newBranch,
          transferredAt: Timestamp.now(),
          transferredBy: auth.currentUser?.uid
        });
      }

      // Update all schedules for this student to new branch
      const studentSchedules = schedules.filter(s => s.studentId === studentId);
      for (const schedule of studentSchedules) {
        const scheduleRef = doc(db, "schedules", schedule.id);
        await updateDoc(scheduleRef, { 
          branch: newBranch,
          transferredAt: Timestamp.now(),
          transferredBy: auth.currentUser?.uid
        });
      }

      // Create notification for student
      const branchName = branches.find(b => b.id === newBranch)?.name || newBranch;
      await addDoc(collection(db, "notifications"), {
        userId: studentId,
        type: "student_transferred",
        title: "Account Transferred",
        message: `Your account has been transferred to ${branchName} branch.`,
        read: false,
        createdAt: Timestamp.now()
      });

      setMessage({ open: true, text: `Student transferred to ${branchName} successfully!`, type: "success" });
      setShowTransferDialog(false);
    } catch (error) {
      console.error("Error transferring student:", error);
      setMessage({ open: true, text: "Failed to transfer student.", type: "error" });
    }
  };

  const formatTime12Hour = (time24: string) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Create enrollment index for faster lookups
  const enrollmentsByStudent = useMemo(() => {
    const map = new Map<string, Enrollment[]>();
    enrollments.forEach(enrollment => {
      const existing = map.get(enrollment.studentId) || [];
      map.set(enrollment.studentId, [...existing, enrollment]);
    });
    return map;
  }, [enrollments]);

  // Helper functions with memoization
  const getStudentEnrollments = useCallback((studentId: string) => {
    return enrollmentsByStudent.get(studentId) || [];
  }, [enrollmentsByStudent]);

  const getStudentSchedules = useCallback((studentId: string) => {
    return schedules.filter(s => s.studentId === studentId);
  }, [schedules]);

  const getEnrollmentProgress = useCallback((enrollment: any) => {
    const course = courses.find(c => c.id === enrollment.courseId);
    const completedSessions = schedules.filter(
      s => s.enrollmentId === enrollment.id && s.status === "completed"
    );
    
    if (!course || !course.totalHours) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    
    const completedHours = completedSessions.reduce((total, session) => {
      const timeSlot = timeSlots.find(ts => ts.id === session.timeSlotId);
      if (timeSlot && timeSlot.startTime && timeSlot.endTime) {
        const [startHour, startMin] = timeSlot.startTime.split(':').map(Number);
        const [endHour, endMin] = timeSlot.endTime.split(':').map(Number);
        const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
        return total + (duration / 60);
      }
      return total + 4; // fallback to 4 hours if time slot not found
    }, 0);
    
    const percentage = Math.min(100, Math.round((completedHours / course.totalHours) * 100));
    
    return {
      completed: completedHours,
      total: course.totalHours,
      percentage
    };
  }, [courses, schedules, timeSlots]);

  // Cache registration types to avoid recalculation
  const registrationTypeCache = useMemo(() => {
    const cache = new Map<string, "online" | "walk-in" | null>();
    
    allStudents.forEach(student => {
      const studentId = student.id!;
      const studentEnrollments = enrollmentsByStudent.get(studentId) || [];
      
      // First check if the student has enrollments to determine type
      if (studentEnrollments.length > 0) {
        // Check if student has any online enrollments (by registrationType field)
        const hasOnlineEnrollment = studentEnrollments.some(e => e.registrationType === "online");
        const hasWalkInEnrollment = studentEnrollments.some(e => e.registrationType === "walk-in");
        
        // Fallback: detect online enrollments by payment receipt upload
        const hasPaymentReceiptUpload = studentEnrollments.some(e => 
          e.paymentReceiptUrl || (e as any).paymentUploaded === true
        );
        
        // Prioritize online if they have both types, or if they have payment receipts (online indicator)
        if (hasOnlineEnrollment || hasPaymentReceiptUpload) {
          cache.set(studentId, "online");
          return;
        }
        if (hasWalkInEnrollment) {
          cache.set(studentId, "walk-in");
          return;
        }
      }
      
      // Fallback: check student's UID for walk-in students (those without enrollments yet)
      if (student.uid?.startsWith("walkin_")) {
        cache.set(studentId, "walk-in");
        return;
      }
      
      // If no enrollments and not a walk-in UID, likely online (Firebase Auth user)
      if (studentEnrollments.length === 0 && student && !student.uid?.startsWith("walkin_")) {
        cache.set(studentId, "online");
        return;
      }
      
      cache.set(studentId, null);
    });
    
    return cache;
  }, [allStudents, enrollmentsByStudent]);

  // Helper function to get student's registration type from cache
  const getStudentRegistrationType = useCallback((studentId: string): "online" | "walk-in" | null => {
    return registrationTypeCache.get(studentId) || null;
  }, [registrationTypeCache]);

  // Helper function to get student preferences - memoized
  const getStudentPreferences = useCallback((studentId: string) => {
    return studentPreferences.find(pref => pref.studentId === studentId);
  }, [studentPreferences]);

  // Filter students based on active tab and search term - memoized for performance
  const filteredStudents = useMemo(() => {
    let filtered = allStudents;
    
    if (activeTab === "online") {
      filtered = allStudents.filter(s => getStudentRegistrationType(s.id!) === "online");
    } else if (activeTab === "walk-in") {
      filtered = allStudents.filter(s => getStudentRegistrationType(s.id!) === "walk-in");
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchLower) ||
        (student.middleName && student.middleName.toLowerCase().includes(searchLower)) ||
        student.phone.includes(searchTerm) ||
        (student.email && student.email.toLowerCase().includes(searchLower)) ||
        (student.ltoClientNumber && student.ltoClientNumber.toLowerCase().includes(searchLower)) ||
        (student.licenseNumber && student.licenseNumber.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [allStudents, activeTab, searchTerm, getStudentRegistrationType]);
  
  // Get paginated students - memoized for performance
  const paginatedStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    return filteredStudents.slice(startIndex, endIndex);
  }, [filteredStudents, currentPage, itemsPerPage]);

  // Calculate total pages based on memoized filteredStudents
  React.useEffect(() => {
    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    setTotalPages(totalPages);
  }, [filteredStudents, itemsPerPage]);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const getStudentStatusBadgeProps = useCallback((student: Student) => {
    const studentEnrollments = getStudentEnrollments(student.id!);
    
    if (studentEnrollments.length === 0) {
      return { variant: "outline" as const, text: "Not Enrolled", className: "!bg-gray-100 !text-gray-700" };
    }
    
    const hasApproved = studentEnrollments.some(e => e.status === "approved");
    const hasPartial = studentEnrollments.some(e => e.status === "partial_payment");
    const hasCompleted = studentEnrollments.some(e => e.status === "completed");
    const hasPending = studentEnrollments.some(e => e.status === "pending");
    
    if (hasCompleted) {
      return { variant: "secondary" as const, text: "Completed", className: "!bg-blue-100 !text-blue-700" };
    }
    if (hasApproved) {
      return { variant: "secondary" as const, text: "Approved", className: "!bg-green-100 !text-green-700" };
    }
    if (hasPartial) {
      return { variant: "secondary" as const, text: "Partial Payment", className: "!bg-yellow-100 !text-yellow-700" };
    }
    if (hasPending) {
      return { variant: "secondary" as const, text: "Pending", className: "!bg-gray-100 !text-gray-700" };
    }
    
    return { variant: "outline" as const, text: "Unknown", className: "" };
  }, [getStudentEnrollments]);

  const getRegistrationTypeBadgeProps = useCallback((registrationType: "online" | "walk-in") => {
    if (registrationType === "walk-in") {
      return { variant: "secondary" as const, text: "Walk-in", className: "!bg-orange-100 !text-orange-700" };
    }
    return { variant: "secondary" as const, text: "Online", className: "!bg-blue-100 !text-blue-700" };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-96 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-40 bg-gray-200 rounded animate-pulse"></div>
        </div>
        
        <div className="space-y-4">
          <div className="h-12 w-full bg-gray-100 rounded animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-48 bg-gray-100 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Get available time slots for a specific date by querying the schedules collection
  const getAvailableTimeSlotsForDate = (selectedDate: string) => {
    if (!selectedDate) return [];
    
    // Get day of week from the selected date
    const dayOfWeek = format(new Date(selectedDate), 'EEEE').toLowerCase();
    
    // Get all time slots for this day of week
    const slotsForDay = timeSlots.filter(slot => slot.dayOfWeek === dayOfWeek);
    
    // Query schedules collection for this specific date
    const schedulesForDate = schedules.filter(schedule => schedule.date === selectedDate);
    
    // Filter out slots that have reached their maximum capacity
    const availableSlots = slotsForDay.filter(slot => {
      const schedulesForSlot = schedulesForDate.filter(schedule => schedule.timeSlotId === slot.id);
      return schedulesForSlot.length < slot.maxStudents;
    });
    
    return availableSlots.sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Enrollment Management</h2>
          <p className="text-muted-foreground">Manage student enrollments, payments, and scheduling</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Walk-in Student
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
        <Button
          variant={activeTab === "online" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("online")}
        >
          Online Students
        </Button>
        <Button
          variant={activeTab === "walk-in" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("walk-in")}
        >
          Walk-in Students
        </Button>
      </div>

      {/* Search and Stats */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Badge variant="outline">
            <Users className="h-3 w-3 mr-1" />
            {filteredStudents.length} Students
          </Badge>
        </div>
      </div>

      {/* Students List */}
      <div className="grid gap-4">
        {filteredStudents.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {searchTerm ? "No students found" : `No ${activeTab} students found`}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? "Try adjusting your search criteria." : `No ${activeTab} students are currently available.`}
              </p>
              {!searchTerm && activeTab === "walk-in" && (
                <Button onClick={() => setShowCreateDialog(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Walk-in Student
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          paginatedStudents.map((student) => {
            const studentEnrollments = getStudentEnrollments(student.id!);
            const studentSchedules = getStudentSchedules(student.id!);
            const hasSchedulableEnrollments = studentEnrollments.some(e => e.status === "approved" || e.status === "partial_payment");
            const hasPartialPayments = studentEnrollments.some(e => e.status === "partial_payment");
            const hasCompletedEnrollments = studentEnrollments.some(e => e.status === "completed" && !e.licenseCode);
            const statusBadge = getStudentStatusBadgeProps(student);
            const registrationType = getStudentRegistrationType(student.id!);
            const regTypeBadge = registrationType ? getRegistrationTypeBadgeProps(registrationType) : null;
            
            return (
              <Card key={student.id} className="overflow-hidden">
                <CardContent className="p-0">
                  {/* Payment Review Banner - Most Prominent */}
                  {(registrationType === "online" || studentEnrollments.some(e => e.paymentReceiptUrl)) && studentEnrollments.some(e => e.status === "pending" && e.paymentReceiptUrl) && (
                    <div className="bg-orange-50 border-b border-orange-200 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 bg-orange-500 rounded-full animate-pulse"></div>
                          <span className="font-medium text-orange-800">Payment Review Required</span>
                        </div>
                        <div className="flex gap-2">
                          {studentEnrollments.filter(e => e.status === "pending" && e.paymentReceiptUrl).map(enrollment => (
                            <div key={enrollment.id} className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(enrollment.paymentReceiptUrl, '_blank')}
                                className="bg-white hover:bg-gray-50"
                              >
                                📄 View Receipt
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                disabled={approvingPayment}
                                onClick={() => handleApprovePayment(enrollment.id)}
                              >
                                {approvingPayment ? "Approving..." : "✓ Approve"}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={rejectingPayment}
                                onClick={() => {
                                  const reason = prompt("Enter rejection reason:");
                                  if (reason && reason.trim()) {
                                    handleRejectPayment(enrollment.id, reason.trim());
                                  }
                                }}
                              >
                                {rejectingPayment ? "Rejecting..." : "✗ Reject"}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Main Content */}
                  <div className="p-6">
                    <div className="flex items-start gap-6">
                      {/* Student Info */}
                      <div className="flex-1 space-y-3 min-w-0">
                        {/* Header with Name and Type */}
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {student.firstName} {student.middleName ? `${student.middleName} ` : ''}{student.lastName}
                          </h3>
                          {regTypeBadge && (
                            <Badge variant={regTypeBadge.variant} className={regTypeBadge.className}>
                              {regTypeBadge.text}
                            </Badge>
                          )}
                          <Badge variant={statusBadge.variant} className={statusBadge.className}>
                            {statusBadge.text}
                          </Badge>
                        </div>

                        {/* Contact Info */}
                        <div className="grid grid-cols-1 gap-4 text-sm text-gray-600">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">📞</span>
                              <span>{student.phone}</span>
                            </div>
                            {student.email && (
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400">✉️</span>
                                <span>{student.email}</span>
                              </div>
                            )}
                            {student.address && (
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400">📍</span>
                                <span>{student.address}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Additional Student Information */}
                          {(student.ltoClientNumber || student.maritalStatus || student.licenseNumber) && (
                            <div className="space-y-1 pt-2 border-t border-gray-200">
                              {student.ltoClientNumber && (
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400 text-xs">LTO #:</span>
                                  <span className="text-xs">{student.ltoClientNumber}</span>
                                </div>
                              )}
                              {student.maritalStatus && (
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400 text-xs">Status:</span>
                                  <span className="text-xs capitalize">{student.maritalStatus}</span>
                                </div>
                              )}
                              {student.licenseNumber && (
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400 text-xs">License #:</span>
                                  <span className="text-xs">{student.licenseNumber}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Enrollment Summary */}
                        {studentEnrollments.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <GraduationCap className="h-4 w-4" />
                                <span>{studentEnrollments.length} Course{studentEnrollments.length !== 1 ? 's' : ''}</span>
                              </div>
                              {studentSchedules.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  <span>{studentSchedules.length} Session{studentSchedules.length !== 1 ? 's' : ''}</span>
                                </div>
                              )}
                            </div>

                            {/* Course Cards */}
                            <div className="space-y-2">
                              {studentEnrollments.map(enrollment => {
                                const course = courses.find(c => c.id === enrollment.courseId);
                                const hasBalance = enrollment.balance > 0;
                                const progress = getEnrollmentProgress(enrollment);
                                
                                return (
                                  <div key={enrollment.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-medium text-gray-900">{course?.title || 'Unknown Course'}</div>
                                        <div className="text-sm text-gray-600">₱{enrollment.courseFee}</div>
                                      </div>
                                      <div className="flex flex-col items-end gap-1">
                                        <Badge 
                                          variant="secondary" 
                                          className={
                                            enrollment.status === "approved" ? "!bg-green-100 !text-green-700" :
                                            enrollment.status === "partial_payment" ? "!bg-yellow-100 !text-yellow-700" :
                                            enrollment.status === "completed" ? "!bg-blue-100 !text-blue-700" :
                                            enrollment.status === "rejected" ? "!bg-red-100 !text-red-700" :
                                            "!bg-gray-100 !text-gray-700"
                                          }
                                        >
                                          {enrollment.status === "partial_payment" ? "Partial Payment" : 
                                           enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                                        </Badge>
                                        {hasBalance && (
                                          <Badge variant="secondary" className="!bg-red-100 !text-red-700 text-xs">
                                            ₱{enrollment.balance} due
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {enrollment.licenseCode && (
                                      <div className="text-sm text-green-700 bg-green-50 rounded px-2 py-1">
                                        🏷️ License Code: {enrollment.licenseCode}
                                      </div>
                                    )}
                                    
                                    {enrollment.licenseCodes && enrollment.licenseCodes.length > 0 && (
                                      <div className="text-sm text-green-700 bg-green-50 rounded px-2 py-1">
                                        <div className="flex justify-between items-center">
                                          <span>🏷️ License Codes: {enrollment.licenseCodes.length}</span>
                                          {enrollment.licenseCodeTotalPrice && (
                                            <span className="font-medium">₱{Number(enrollment.licenseCodeTotalPrice).toLocaleString()}</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {enrollment.status === "approved" && progress.total > 0 && (
                                      <div className="space-y-1">
                                        <div className="flex justify-between text-sm text-gray-600">
                                          <span>Progress: {progress.completed}h / {progress.total}h</span>
                                          <span>{progress.percentage}%</span>
                                        </div>
                                        <Progress value={progress.percentage} className="h-2" />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons - Compact design */}
                      <div className="flex flex-col gap-1.5 flex-shrink-0 w-32">
                        {/* View Details button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedStudent(student);
                            setShowDetailsDialog(true);
                          }}
                          className="text-xs h-7"
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          Details
                        </Button>

                        {/* Edit Student button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(student)}
                          className="text-xs h-7"
                        >
                          <Edit2 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>

                        {/* Transfer Student button */}
                        <Button
                          variant="outline"
                          size="sm"
                            onClick={() => {
                            setSelectedStudent(student);
                            setShowTransferDialog(true);
                          }}
                          className="text-xs h-7"
                        >
                          <ArrowRightLeft className="h-3 w-3 mr-1" />
                          Transfer
                        </Button>

                        {/* Enroll button - Always visible */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedStudent(student);
                            setShowEnrollDialog(true);
                          }}
                          className="text-xs h-7"
                        >
                          <GraduationCap className="h-3 w-3 mr-1" />
                          Enroll
                        </Button>
                        
                        {/* 1. Schedule - Priority action for approved/partial_payment enrollments */}
                        {hasSchedulableEnrollments && (() => {
                          const schedulableCount = studentEnrollments.filter(e => e.status === "approved" || e.status === "partial_payment").length;
                          return (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedStudent(student);
                                setSelectedEnrollment(null);
                                setShowScheduleDialog(true);
                              }}
                              title={schedulableCount > 1 ? `Schedule sessions for ${schedulableCount} courses` : "Schedule session"}
                            >
                              <CalendarClock className="h-4 w-4 mr-1" />
                              Schedule{schedulableCount > 1 ? ` (${schedulableCount})` : ''}
                            </Button>
                          );
                        })()}
                        
                        {/* 2. View Sessions - For students with existing schedules */}
                        {studentSchedules.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowSessionsDialog(true);
                            }}
                          >
                            <Clock className="h-4 w-4 mr-1" />
                            View Sessions
                          </Button>
                        )}

                        {/* 3. Upload Certificates - For paid enrollments */}
                        {studentEnrollments.some(e => e.status === "approved" || e.status === "completed" || e.status === "partial_payment") && (() => {
                          const enrollmentWithCertificate = studentEnrollments.find(e => e.certificateUrl);
                          const enrollmentNeedingCertificate = studentEnrollments.find(e => 
                            (e.status === "approved" || e.status === "completed" || e.status === "partial_payment") && !e.certificateUrl
                          );
                          
                          if (enrollmentWithCertificate) {
                            return (
                              <div className="flex flex-col gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(enrollmentWithCertificate.certificateUrl, '_blank')}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  View Certificate
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedEnrollment(enrollmentWithCertificate);
                                    setShowUploadDialog(true);
                                  }}
                                >
                                  <Upload className="h-4 w-4 mr-1" />
                                  Replace Certificate
                                </Button>
                              </div>
                            );
                          } else if (enrollmentNeedingCertificate) {
                            return (
                              <div className="flex flex-col gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedEnrollment(enrollmentNeedingCertificate);
                                    setShowUploadDialog(true);
                                  }}
                                >
                                  <Upload className="h-4 w-4 mr-1" />
                                  Upload Certificate
                                </Button>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* Additional Payment Button for partial payments */}
                        {hasPartialPayments && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedStudent(student);
                              const partialEnrollment = studentEnrollments.find(e => e.status === "partial_payment");
                              setSelectedEnrollment(partialEnrollment || null);
                              if (partialEnrollment) {
                                setPaymentData({ ...paymentData, amount: partialEnrollment.balance || 0 });
                              }
                              setShowPaymentDialog(true);
                            }}
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            Add Payment
                          </Button>
                        )}

                        {/* 2.5. Mark as Complete - For enrollments with 100% progress but not completed status */}
                        {studentEnrollments.some(e => {
                          if (e.status !== "approved") return false;
                          const progress = getEnrollmentProgress(e);
                          return progress.percentage >= 100;
                        }) && (() => {
                          const completableCount = studentEnrollments.filter(e => {
                            if (e.status !== "approved") return false;
                            const progress = getEnrollmentProgress(e);
                            return progress.percentage >= 100;
                          }).length;
                          
                          return (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedStudent(student);
                                setSelectedEnrollment(null);
                                setShowCompletionDialog(true);
                              }}
                              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                              title={completableCount > 1 ? `${completableCount} courses ready for completion` : "Course ready for completion"}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Mark as Complete{completableCount > 1 ? ` (${completableCount})` : ''}
                            </Button>
                          );
                        })()}

                        {/* 2.6. Quick Complete - For any approved/partial_payment enrollments */}
                        {studentEnrollments.some(e => e.status === "approved" || e.status === "partial_payment") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowQuickCompleteDialog(true);
                            }}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                            title="Mark any enrolled course as complete"
                          >
                            <Award className="h-4 w-4 mr-1" />
                            Quick Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center mt-6">
          <div className="flex items-center gap-2 bg-white p-3 rounded-lg border shadow-sm">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="h-8"
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else {
                  if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className="w-8 h-8"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="h-8"
            >
              Next
            </Button>
            <span className="text-xs text-muted-foreground ml-2">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        </div>
      )}

      {/* Create Student Dialog */}
      <UIDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <UIDialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <UIDialogHeader>
            <UIDialogTitle>Add Walk-in Student</UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleCreateStudent} className="flex-1 flex flex-col">
            <div className="space-y-4 flex-1 overflow-y-auto max-h-[60vh] pr-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={newStudent.firstName || ""}
                    onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={newStudent.lastName || ""}
                    onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="middleName">Middle Name</Label>
                <Input
                  id="middleName"
                  value={newStudent.middleName || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, middleName: e.target.value })}
                  placeholder="Enter middle name"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={newStudent.phone || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                  placeholder="09XXXXXXXXX"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={newStudent.email || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                  placeholder="student@example.com"
                />
              </div>
              
              <div>
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={newStudent.address || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, address: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="ltoClientNumber">LTO Client Number</Label>
                <Input
                  id="ltoClientNumber"
                  value={newStudent.ltoClientNumber || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, ltoClientNumber: e.target.value })}
                  placeholder="Enter LTO client number"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={newStudent.dateOfBirth || ""}
                    onChange={(e) => setNewStudent({ ...newStudent, dateOfBirth: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender *</Label>
                  <select
                    id="gender"
                    value={newStudent.gender || ""}
                    onChange={(e) => setNewStudent({ ...newStudent, gender: e.target.value })}
                    className="w-full border rounded p-2"
                    required
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="maritalStatus">Marital Status</Label>
                  <select
                    id="maritalStatus"
                    value={newStudent.maritalStatus || ""}
                    onChange={(e) => setNewStudent({ ...newStudent, maritalStatus: e.target.value })}
                    className="w-full border rounded p-2"
                  >
                    <option value="">Select marital status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="licenseNumber">Student/Driver License Number</Label>
                  <Input
                    id="licenseNumber"
                    value={newStudent.licenseNumber || ""}
                    onChange={(e) => setNewStudent({ ...newStudent, licenseNumber: e.target.value })}
                    placeholder="Enter license number (if applicable)"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? "Creating..." : "Create Student"}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* Enroll Student Dialog */}
      <UIDialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Enroll {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleEnrollStudent} className="space-y-4">
            <div>
              <Label htmlFor="courseId">Course *</Label>
              <select
                id="courseId"
                value={enrollmentData.courseId}
                onChange={(e) => {
                  const courseId = e.target.value;
                  const course = courses.find(c => c.id === courseId);
                  setEnrollmentData({ 
                    ...enrollmentData, 
                    courseId,
                    paymentAmount: course?.price || 0
                  });
                }}
                className="w-full border rounded p-2"
                required
              >
                <option value="">Select a course</option>
                {courses
                  .filter(course => {
                    // Filter courses to show only those available for the staff member's branch
                    // If course has no branches array, show it (for backward compatibility)
                    // If course has branches array, show it only if staff's branch is included
                    return !course.branches || course.branches.length === 0 || course.branches.includes(userBranch);
                  })
                  .map(course => (
                    <option key={course.id} value={course.id}>
                      {course.title} - ₱{course.price}
                    </option>
                  ))}
              </select>
            </div>
            
              {/* Discount Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="applyDiscount"
                  checked={!!enrollmentData.discountApplied}
                  onChange={e => setEnrollmentData({ ...enrollmentData, discountApplied: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="applyDiscount">Apply Discount</Label>
              </div>

              {/* Discount Fields - show only if checked */}
              {enrollmentData.discountApplied && (
                <div className="space-y-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <div>
                    <Label htmlFor="discountType">Discount Type</Label>
                    <select
                      id="discountType"
                      value={enrollmentData.discountType || ""}
                      onChange={e => setEnrollmentData({ ...enrollmentData, discountType: e.target.value })}
                      className="w-full border rounded p-2"
                    >
                      <option value="">Select type</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="percent">Percent</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="discountAmount">Discount Amount</Label>
                    <Input
                      id="discountAmount"
                      type="number"
                      value={enrollmentData.discountAmount || ""}
                      onChange={e => setEnrollmentData({ ...enrollmentData, discountAmount: Number(e.target.value) })}
                      min="0"
                      step="0.01"
                      placeholder="Enter discount amount"
                    />
                  </div>
                  <div>
                    <Label htmlFor="discountReason">Discount Reason</Label>
                    <Input
                      id="discountReason"
                      value={enrollmentData.discountReason || ""}
                      onChange={e => setEnrollmentData({ ...enrollmentData, discountReason: e.target.value })}
                      placeholder="Enter reason for discount"
                    />
                  </div>
                </div>
              )}
            <div>
              <Label htmlFor="paymentMethod">Payment Method</Label>
              <select
                id="paymentMethod"
                value={enrollmentData.paymentMethod}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, paymentMethod: e.target.value })}
                className="w-full border rounded p-2"
              >
                <option value="cash">Cash</option>
                <option value="gcash">GCash</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="paymentAmount">Amount Received</Label>
              <Input
                id="paymentAmount"
                type="number"
                value={enrollmentData.paymentAmount}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, paymentAmount: Number(e.target.value) })}
                min="0"
                step="0.01"
              />
              {enrollmentData.courseId && (
                <div className="text-sm text-muted-foreground mt-1">
                  {(() => {
                    const courseFee = courses.find(c => c.id === enrollmentData.courseId)?.price || 0;
                    const discount = enrollmentData.discountApplied ? (Number(enrollmentData.discountAmount) || 0) : 0;
                    const netFee = Math.max(0, courseFee - discount);
                    const balance = Math.max(0, netFee - enrollmentData.paymentAmount);
                    return `Course Fee: ₱${courseFee} | Discount: ₱${discount} | Balance: ₱${balance}`;
                  })()}
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                value={enrollmentData.notes}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, notes: e.target.value })}
                placeholder="Additional notes about this enrollment"
              />
            </div>
            
            {/* OR Number fields - only show if payment amount is greater than 0 */}
            {enrollmentData.paymentAmount > 0 && (
              <div className="space-y-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="text-sm font-medium text-blue-800">OR Number</div>
                <div>
                  <Label htmlFor="orNumber">OR Number</Label>
                  <div className="relative">
                    <Input
                      id="orNumber"
                      value={enrollmentData.orNumber || ""}
                      onChange={(e) => setEnrollmentData({ ...enrollmentData, orNumber: e.target.value })}
                      placeholder="Type OR number..."
                      className={(() => {
                        const orNumber = enrollmentData.orNumber || "";
                        const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
                        const isValid = availableNumbers.includes(orNumber);
                        const isTyping = orNumber.length > 0;
                        
                        if (isTyping && !isValid) {
                          return "border-red-500 focus:ring-red-500";
                        } else if (isTyping && isValid) {
                          return "border-green-500 focus:ring-green-500";
                        }
                        return "";
                      })()}
                    />
                    
                    {/* Autocomplete suggestions */}
                    {(enrollmentData.orNumber || "").length > 0 && (() => {
                      const orNumber = enrollmentData.orNumber || "";
                      const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
                      const filteredNumbers = availableNumbers.filter(num => 
                        num.toLowerCase().includes(orNumber.toLowerCase())
                      );
                      
                      if (filteredNumbers.length > 0 && !availableNumbers.includes(orNumber)) {
                        return (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                            {filteredNumbers.slice(0, 10).map(num => (
                              <div
                                key={num}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onClick={() => setEnrollmentData({ ...enrollmentData, orNumber: num })}
                              >
                                {num}
                              </div>
                            ))}
                            {filteredNumbers.length > 10 && (
                              <div className="px-3 py-2 text-xs text-gray-500 border-t">
                                And {filteredNumbers.length - 10} more...
                              </div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Validation message */}
                    {(enrollmentData.orNumber || "").length > 0 && (() => {
                      const orNumber = enrollmentData.orNumber || "";
                      const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
                      const isValid = availableNumbers.includes(orNumber);
                      
                      if (!isValid) {
                        return (
                          <p className="text-sm text-red-600 mt-1">
                            OR number "{orNumber}" does not exist or is not available
                          </p>
                        );
                      } else {
                        return (
                          <p className="text-sm text-green-600 mt-1">
                            ✓ OR number is valid and available
                          </p>
                        );
                      }
                    })()}
                  </div>
                  
                  {orBatches.flatMap(batch => batch.availableNumbers).length === 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      No OR numbers available for this branch. Please contact admin to generate more OR numbers.
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="orNotes">OR Notes</Label>
                  <Input
                    id="orNotes"
                    value={enrollmentData.orNotes || ""}
                    onChange={(e) => setEnrollmentData({ ...enrollmentData, orNotes: e.target.value })}
                    placeholder="Additional notes for OR"
                  />
                </div>
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEnrollDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={enrolling}>
                {enrolling ? "Enrolling..." : "Enroll Student"}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* Schedule Student Dialog */}
      <UIDialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Schedule Session for {selectedStudent?.firstName} {selectedStudent?.lastName}
              {selectedStudent && (() => {
                const schedulableCount = getStudentEnrollments(selectedStudent.id!).filter(e => e.status === "approved" || e.status === "partial_payment").length;
                return schedulableCount > 1 ? (
                  <div className="text-sm font-normal text-muted-foreground mt-1">
                    Student has {schedulableCount} courses - select which course to schedule
                  </div>
                ) : null;
              })()}
            </UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleScheduleStudent} className="space-y-4">
            {/* Course Selection */}
            {selectedStudent && (() => {
              const schedulableEnrollments = getStudentEnrollments(selectedStudent.id!).filter(e => e.status === "approved" || e.status === "partial_payment");
              
              return (
                <div>
                  <Label htmlFor="enrollmentId" className="text-sm font-medium">
                    Select Course to Schedule * 
                    {schedulableEnrollments.length > 1 && (
                      <span className="ml-1 text-blue-600 font-normal">
                        ({schedulableEnrollments.length} courses available)
                      </span>
                    )}
                  </Label>
                  {schedulableEnrollments.length === 0 ? (
                    <div className="w-full border rounded p-2 bg-gray-50 text-gray-500">
                      No schedulable courses found for this student
                    </div>
                  ) : (
                    <select
                      id="enrollmentId"
                      value={selectedEnrollment?.id || ""}
                      onChange={(e) => {
                        const enrollment = schedulableEnrollments.find(en => en.id === e.target.value);
                        setSelectedEnrollment(enrollment || null);
                      }}
                      className="w-full border rounded p-2"
                      required
                    >
                      <option value="">
                        {schedulableEnrollments.length === 1 
                          ? "Select the course" 
                          : `Choose from ${schedulableEnrollments.length} courses`
                        }
                      </option>
                      {schedulableEnrollments.map((enrollment, index) => {
                        const course = courses.find(c => c.id === enrollment.courseId);
                        const sessionsCount = schedules.filter(s => 
                          s.enrollmentId === enrollment.id && s.status !== "cancelled"
                        ).length;
                        
                        return (
                          <option key={enrollment.id} value={enrollment.id}>
                            {index + 1}. {course?.title || 'Unknown Course'} 
                            {sessionsCount > 0 && ` (${sessionsCount} sessions)`}
                            {enrollment.balance > 0 ? ` - ₱${enrollment.balance} balance` : ' - Paid'}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              );
            })()}

            {/* Show course details when selected */}
            {selectedEnrollment && (() => {
              const course = courses.find(c => c.id === selectedEnrollment.courseId);
              const existingSessions = schedules.filter(s => 
                s.enrollmentId === selectedEnrollment.id && s.status !== "cancelled"
              );
              const completedSessions = existingSessions.filter(s => s.status === "completed");
              const scheduledSessions = existingSessions.filter(s => s.status === "scheduled");
              
              return (
                <div className="p-3 bg-muted rounded border-l-4 border-blue-500">
                  <div className="text-sm font-medium text-blue-800 mb-2">
                    📚 Course Details
                  </div>
                  <div className="text-sm">
                    <strong>Course:</strong> {course?.title || 'Unknown'}
                  </div>
                  <div className="text-sm">
                    <strong>Fee:</strong> ₱{selectedEnrollment.courseFee}
                    {selectedEnrollment.balance > 0 && (
                      <span className="text-destructive ml-2">
                        (₱{selectedEnrollment.balance} balance)
                      </span>
                    )}
                  </div>
                  <div className="text-sm">
                    <strong>Sessions:</strong> {completedSessions.length} completed, {scheduledSessions.length} scheduled
                  </div>
                  {course?.duration && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Total course duration: {course.duration} hours
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Student Preferences Section */}
            {selectedStudent && (() => {
              const preferences = getStudentPreferences(selectedStudent.id!);
              if (!preferences) return null;
              
              return (                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-sm font-medium text-blue-800 mb-2">
                        📋 Student's Preferred Schedule
                      </div>
                      {preferences.preferredDays?.length > 0 && (
                        <div className="text-sm text-blue-700 mb-2">
                          <strong>Preferred Days:</strong> {preferences.preferredDays.map((day: string) => 
                            day.charAt(0).toUpperCase() + day.slice(1)
                          ).join(", ")}
                        </div>
                      )}
                      {preferences.preferredTimeSessions?.length > 0 && (
                        <div className="text-sm text-blue-700 mb-2">
                          <strong>Preferred Sessions:</strong>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {preferences.preferredTimeSessions.map((session: string) => (
                              <span key={session} className="text-xs bg-blue-100 rounded px-2 py-1 capitalize">
                                {session} Session
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {preferences.notes && (
                        <div className="text-sm text-blue-700">
                          <strong>Notes:</strong> {preferences.notes}
                        </div>
                      )}
                    </div>
              );
            })()}
            
            <div>
              <Label htmlFor="scheduleDate">Date *</Label>
              <Input
                id="scheduleDate"
                type="date"
                value={scheduleData.date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  setScheduleData({ ...scheduleData, date: selectedDate, timeSlotId: "" });
                }}
                required
              />
            </div>

            {/* Only render the time slot dropdown if a valid date is selected (YYYY-MM-DD) */}
            {(!!scheduleData.date && scheduleData.date.length === 10) && (
              <div>
                <Label htmlFor="timeSlotId">Available Time Slots *</Label>
                <select
                  id="timeSlotId"
                  value={scheduleData.timeSlotId}
                  onChange={(e) => setScheduleData({ ...scheduleData, timeSlotId: e.target.value })}
                  className="w-full border rounded p-2"
                  required
                >
                  <option value="">Select a time slot</option>
                  {(() => {
                    const availableSlots = getAvailableTimeSlotsForDate(scheduleData.date);
                    if (availableSlots.length === 0) {
                      return <option disabled>No available time slots for this day</option>;
                    }
                    return availableSlots.map(slot => (
                      <option key={slot.id} value={slot.id}>
                        {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                      </option>
                    ));
                  })()}
                </select>
                {scheduleData.date && timeSlots.filter(slot => 
                  slot.dayOfWeek === format(new Date(scheduleData.date), 'EEEE').toLowerCase()
                ).length === 0 && (
                  <div className="text-sm text-muted-foreground mt-1">
                    No time slots configured for {format(new Date(scheduleData.date), 'EEEE')}s
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="instructorId">Instructor *</Label>
              <select
                id="instructorId"
                value={scheduleData.instructorId}
                onChange={(e) => setScheduleData({ ...scheduleData, instructorId: e.target.value })}
                className="w-full border rounded p-2"
                required
              >
                <option value="">Select an instructor</option>
                {instructors.map(instructor => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.firstName} {instructor.lastName}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="scheduleNotes">Notes (Optional)</Label>
              <Input
                id="scheduleNotes"
                value={scheduleData.notes}
                onChange={(e) => setScheduleData({ ...scheduleData, notes: e.target.value })}
                placeholder="Additional notes for this session"
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowScheduleDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={scheduling}>
                {scheduling ? "Scheduling..." : "Schedule Session"}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* Sessions Management Dialog */}
      <UIDialog open={showSessionsDialog} onOpenChange={setShowSessionsDialog}>
        <UIDialogContent className="max-w-2xl">
          <UIDialogHeader>
            <UIDialogTitle>
              Sessions for {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          <div className="space-y-4">
            {selectedStudent && (() => {
              const studentSchedules = getStudentSchedules(selectedStudent.id!);
              const sortedSchedules = studentSchedules.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              
              if (sortedSchedules.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    No sessions scheduled yet.
                  </div>
                );
              }
              
              return sortedSchedules.map(schedule => {
                const course = courses.find(c => c.id === schedule.courseId);
                const instructor = instructors.find(i => i.id === schedule.instructorId);
                const timeSlot = timeSlots.find(ts => ts.id === schedule.timeSlotId);
                
                return (
                  <Card key={schedule.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2 flex-1">
                          <div className="font-medium">{course?.title || 'Unknown Course'}</div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <div>📅 {new Date(schedule.date).toLocaleDateString()}</div>
                            <div>🕐 {schedule.time}</div>
                            <div>👨‍🏫 {instructor?.firstName} {instructor?.lastName}</div>
                            {schedule.notes && <div>📝 {schedule.notes}</div>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Badge variant={
                            schedule.status === "completed" ? "default" :
                            schedule.status === "cancelled" ? "destructive" :
                            "secondary"
                          }>
                            {schedule.status}
                          </Badge>
                          {schedule.status === "scheduled" && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSchedule(schedule);
                                  setRescheduleData({
                                    timeSlotId: schedule.timeSlotId,
                                    date: schedule.date,
                                    instructorId: schedule.instructorId,
                                    notes: schedule.notes || "",
                                  });
                                  setShowRescheduleDialog(true);
                                }}
                                className="ml-2"
                              >
                                <CalendarClock className="h-4 w-4 mr-1" />
                                Reschedule
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCompleteSession(schedule.id!, course?.hours || 2);
                                }}
                                disabled={completingSession === schedule.id}
                                className="ml-2"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {completingSession === schedule.id ? "Completing..." : "Complete"}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              });
            })()}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowSessionsDialog(false)}>
              Close
            </Button>
          </div>
        </UIDialogContent>
      </UIDialog>

      {/* Payment Dialog */}
      <UIDialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Add Payment for {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          {selectedEnrollment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded">
                <div className="text-sm space-y-1">
                  <div><strong>Course:</strong> {courses.find(c => c.id === selectedEnrollment.courseId)?.title || 'Unknown'}</div>
                  <div><strong>Course Fee:</strong> ₱{selectedEnrollment.courseFee}</div>
                  <div><strong>Amount Received:</strong> ₱{selectedEnrollment.amountReceived}</div>
                  <div className="text-destructive"><strong>Outstanding Balance:</strong> ₱{selectedEnrollment.balance}</div>
                </div>
              </div>
              
              <form onSubmit={handleAddPayment} className="space-y-4">
                <div>
                  <Label htmlFor="paymentAmount">Payment Amount *</Label>
                  <Input
                    id="paymentAmount"
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: Number(e.target.value) })}
                    min="0.01"
                    max={selectedEnrollment.balance}
                    step="0.01"
                    required
                  />
                  <div className="text-sm text-muted-foreground mt-1">
                    New balance after payment: ₱{Math.max(0, selectedEnrollment.balance - paymentData.amount)}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="paymentMethod">Payment Method</Label>
                  <select
                    id="paymentMethod"
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                    className="w-full border rounded p-2"
                  >
                    <option value="cash">Cash</option>
                    <option value="gcash">GCash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="paymentNotes">Notes (Optional)</Label>
                  <Input
                    id="paymentNotes"
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    placeholder="Additional notes for this payment"
                  />
                </div>
                
                {/* OR Number Section */}
                <div className="space-y-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="text-sm font-medium text-blue-800">
                    📄 Official Receipt (OR) - Optional
                  </div>
                  
                  <div>
                    <Label htmlFor="orNumber">OR Number</Label>
                    <div className="relative">
                      <Input
                        id="orNumber"
                        value={paymentData.orNumber}
                        onChange={(e) => setPaymentData({ ...paymentData, orNumber: e.target.value })}
                        placeholder="Type OR number..."
                        className={(() => {
                          const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
                          const isValid = availableNumbers.includes(paymentData.orNumber);
                          const isTyping = paymentData.orNumber.length > 0;
                          
                          if (isTyping && !isValid) {
                            return "border-red-500 focus:ring-red-500";
                          } else if (isTyping && isValid) {
                            return "border-green-500 focus:ring-green-500";
                          }
                          return "";
                        })()}
                      />
                      
                      {/* Autocomplete suggestions */}
                      {paymentData.orNumber.length > 0 && (() => {
                        const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
                        const filteredNumbers = availableNumbers.filter(num => 
                          num.toLowerCase().includes(paymentData.orNumber.toLowerCase())
                        );
                        
                        if (filteredNumbers.length > 0 && !availableNumbers.includes(paymentData.orNumber)) {
                          return (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {filteredNumbers.slice(0, 10).map(num => (
                                <div
                                  key={num}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                  onClick={() => setPaymentData({ ...paymentData, orNumber: num })}
                                >
                                  {num}
                                </div>
                              ))}
                              {filteredNumbers.length > 10 && (
                                <div className="px-3 py-2 text-xs text-gray-500 border-t">
                                  And {filteredNumbers.length - 10} more...
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Validation message */}
                      {paymentData.orNumber.length > 0 && (() => {
                        const availableNumbers = orBatches.flatMap(batch => batch.availableNumbers);
                        const isValid = availableNumbers.includes(paymentData.orNumber);
                        
                        if (!isValid) {
                          return (
                            <p className="text-sm text-red-600 mt-1">
                              OR number "{paymentData.orNumber}" does not exist or is not available
                            </p>
                          );
                        } else {
                          return (
                            <p className="text-sm text-green-600 mt-1">
                              ✓ OR number is valid and available
                            </p>
                          );
                        }
                      })()}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="orNotes">OR Notes (Optional)</Label>
                    <Input
                      id="orNotes"
                      value={paymentData.orNotes}
                      onChange={(e) => setPaymentData({ ...paymentData, orNotes: e.target.value })}
                      placeholder="Additional notes for this OR"
                    />
                  </div>
                  
                  {orBatches.flatMap(batch => batch.availableNumbers).length === 0 && (
                    <p className="text-sm text-amber-600">
                      No OR numbers available for this branch. Contact admin to generate more OR numbers.
                    </p>
                  )}
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processingPayment}>
                    {processingPayment ? "Recording..." : "Record Payment"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </UIDialogContent>
      </UIDialog>

      {/* Reschedule Session Dialog */}
      <UIDialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Reschedule Session for {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          {selectedSchedule && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded">
                <div className="text-sm space-y-1">
                  <div><strong>Current Session:</strong></div>
                  <div>📅 {new Date(selectedSchedule.date).toLocaleDateString()}</div>
                  <div>🕐 {selectedSchedule.time}</div>
                  <div>👨‍🏫 {instructors.find(i => i.id === selectedSchedule.instructorId)?.firstName} {instructors.find(i => i.id === selectedSchedule.instructorId)?.lastName}</div>
                </div>
              </div>

              {/* Student Preferences Section */}
              {selectedStudent && (() => {
                const preferences = getStudentPreferences(selectedStudent.id!);
                if (!preferences) return null;
                
                return (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-sm font-medium text-blue-800 mb-2">
                      📋 Student's Preferred Schedule
                    </div>
                    {preferences.preferredDays?.length > 0 && (
                      <div className="text-sm text-blue-700 mb-2">
                        <strong>Preferred Days:</strong> {preferences.preferredDays.map((day: string) => 
                          day.charAt(0).toUpperCase() + day.slice(1)
                        ).join(", ")}
                      </div>
                    )}
                    {preferences.preferredTimeSessions?.length > 0 && (
                      <div className="text-sm text-blue-700 mb-2">
                        <strong>Preferred Sessions:</strong>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {preferences.preferredTimeSessions.map((session: string) => (
                            <span key={session} className="text-xs bg-blue-100 rounded px-2 py-1 capitalize">
                              {session} Session
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {preferences.notes && (
                      <div className="text-sm text-blue-700">
                        <strong>Notes:</strong> {preferences.notes}
                      </div>
                    )}
                  </div>
                );
              })()}
              
              <form onSubmit={handleRescheduleSession} className="space-y-4">
                <div>
                  <Label htmlFor="rescheduleDate">New Date *</Label>
                  <Input
                    id="rescheduleDate"
                    type="date"
                    value={rescheduleData.date}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setRescheduleData({ ...rescheduleData, date: selectedDate, timeSlotId: "" });
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                {/* Only render the time slot dropdown if a valid date is selected */}
                {(!!rescheduleData.date && rescheduleData.date.length === 10) && (
                  <div>
                    <Label htmlFor="rescheduleTimeSlotId">Available Time Slots *</Label>
                    <select
                      id="rescheduleTimeSlotId"
                      value={rescheduleData.timeSlotId}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, timeSlotId: e.target.value })}
                      className="w-full border rounded p-2"
                      required
                    >
                      <option value="">Select a time slot</option>
                      {(() => {
                        const availableSlots = getAvailableTimeSlotsForDate(rescheduleData.date);
                        // Include current slot if it's the same date (allow keeping same time)
                        if (rescheduleData.date === selectedSchedule.date) {
                          const currentSlot = timeSlots.find(ts => ts.id === selectedSchedule.timeSlotId);
                          if (currentSlot && !availableSlots.find(slot => slot.id === currentSlot.id)) {
                            availableSlots.push(currentSlot);
                            availableSlots.sort((a, b) => a.startTime.localeCompare(b.startTime));
                          }
                        }
                        
                        if (availableSlots.length === 0) {
                          return <option disabled>No available time slots for this day</option>;
                        }
                        return availableSlots.map(slot => (
                          <option key={slot.id} value={slot.id}>
                            {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                          </option>
                        ));
                      })()}
                    </select>
                    {rescheduleData.date && timeSlots.filter(slot => 
                      slot.dayOfWeek === format(new Date(rescheduleData.date), 'EEEE').toLowerCase()
                    ).length === 0 && (
                      <div className="text-sm text-muted-foreground mt-1">
                        No time slots configured for {format(new Date(rescheduleData.date), 'EEEE')}s
                      </div>
                    )}
                  </div>
                )}
                
                <div>
                  <Label htmlFor="rescheduleInstructorId">Instructor *</Label>
                  <select
                    id="rescheduleInstructorId"
                    value={rescheduleData.instructorId}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, instructorId: e.target.value })}
                    className="w-full border rounded p-2"
                    required
                  >
                    <option value="">Select an instructor</option>
                    {instructors.map(instructor => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.firstName} {instructor.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="rescheduleNotes">Notes (Optional)</Label>
                  <Input
                    id="rescheduleNotes"
                    value={rescheduleData.notes}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, notes: e.target.value })}
                    placeholder="Reason for rescheduling or additional notes"
                  />
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowRescheduleDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={rescheduling}>
                    {rescheduling ? "Rescheduling..." : "Reschedule Session"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </UIDialogContent>
      </UIDialog>

      {/* Certificate Upload Dialog */}
      <UIDialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              {selectedEnrollment?.certificateUrl ? "Replace Certificate" : "Upload Certificate"}
            </UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleUploadCertificate} className="space-y-4">
            {selectedEnrollment && (
              <div className="p-3 bg-muted rounded">
                <div className="text-sm space-y-1">
                  <div><strong>Student:</strong> {selectedStudent?.firstName} {selectedStudent?.lastName}</div>
                  <div><strong>Course:</strong> {courses.find(c => c.id === selectedEnrollment.courseId)?.title}</div>
                  {selectedEnrollment.certificateUrl && (
                    <div><strong>Action:</strong> Replacing existing certificate</div>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="certificateFile">Certificate File *</Label>
              <Input
                id="certificateFile"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setSelectedCertificateFile(e.target.files?.[0] || null)}
                required
              />
              <div className="text-xs text-muted-foreground mt-1">
                Supported formats: PDF, JPG, PNG (Max 10MB)
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !selectedCertificateFile}>
                {uploading ? "Uploading..." : selectedEnrollment?.certificateUrl ? "Replace Certificate" : "Upload Certificate"}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* License Code Assignment Dialog */}
      <UIDialog open={showLicenseAssignDialog} onOpenChange={setShowLicenseAssignDialog}>
        <UIDialogContent className="max-w-lg">
          <UIDialogHeader>
            <UIDialogTitle>
              Assign License Codes
            </UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleAssignMultipleLicenseCodes} className="space-y-4">
            {selectedEnrollment && (
              <div className="p-3 bg-muted rounded">
                <div className="text-sm space-y-1">
                  <div><strong>Student:</strong> {selectedStudent?.firstName} {selectedStudent?.lastName}</div>
                  <div><strong>Course:</strong> {courses.find(c => c.id === selectedEnrollment.courseId)?.title}</div>
                  {selectedEnrollment.licenseCodes && selectedEnrollment.licenseCodes.length > 0 && (
                    <div>
                      <strong>Currently Assigned:</strong>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedEnrollment.licenseCodes.map(codeId => {
                          const code = allLicenseCodes.find(lc => lc.id === codeId);
                          return code ? (
                            <span key={codeId} className="text-xs bg-blue-100 rounded px-2 py-1">
                              {code.code}
                            </span>
                          ) : null;
                        })}
                      </div>
                      <div className="text-sm text-green-700 mt-1">
                        <strong>Current Total Cost: ₱{(Number(selectedEnrollment.licenseCodeTotalPrice) || 0).toLocaleString()}</strong>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <Label>Available License Codes</Label>
              <div className="max-h-60 overflow-y-auto border rounded p-3 space-y-2">
                {allLicenseCodes
                  .filter(lc => lc.isActive !== false)
                  .filter(lc => {
                    // Filter by branch - show license codes that:
                    // 1. Have no branches assigned (legacy codes), or
                    // 2. Include the current user's branch in their branches array
                    return !lc.branches || lc.branches.length === 0 || lc.branches.includes(userBranch);
                  })
                  .filter(lc => {
                    // Filter out license codes that are already assigned to this enrollment
                    const currentLicenseCodeIds = selectedEnrollment?.licenseCodeIds || [];
                    return !currentLicenseCodeIds.includes(lc.id);
                  })
                  .map(code => (
                  <div key={code.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`license-${code.id}`}
                      checked={selectedLicenseCodes.includes(code.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLicenseCodes([...selectedLicenseCodes, code.id]);
                        } else {
                          setSelectedLicenseCodes(selectedLicenseCodes.filter(id => id !== code.id));
                        }
                      }}
                      className="rounded"
                    />
                    <label htmlFor={`license-${code.id}`} className="flex-1 text-sm cursor-pointer">
                      <div className="flex justify-between">
                        <span><strong>{code.code}</strong> - {code.description}</span>
                        <span className="text-muted-foreground">₱{code.price}</span>
                      </div>
                    </label>
                  </div>
                ))}
                {allLicenseCodes
                  .filter(lc => lc.isActive !== false)
                  .filter(lc => {
                    // Filter by branch - show license codes that:
                    // 1. Have no branches assigned (legacy codes), or
                    // 2. Include the current user's branch in their branches array
                    return !lc.branches || lc.branches.length === 0 || lc.branches.includes(userBranch);
                  })
                  .filter(lc => {
                    const currentLicenseCodeIds = selectedEnrollment?.licenseCodeIds || [];
                    return !currentLicenseCodeIds.includes(lc.id);
                  }).length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    All available license codes have been assigned to this student.
                  </div>
                )}
              </div>
              {selectedLicenseCodes.length > 0 && (() => {
                const selectedCodeDetails = allLicenseCodes.filter(lc => selectedLicenseCodes.includes(lc.id));
                const totalNewCost = selectedCodeDetails.reduce((sum, lc) => sum + (Number(lc.price) || 0), 0);
                const currentTotal = Number(selectedEnrollment?.licenseCodeTotalPrice) || 0;
                const grandTotal = currentTotal + totalNewCost;
                
                return (
                  <div className="text-sm bg-green-50 border border-green-200 rounded p-3 mt-2">
                    <div className="font-medium text-green-800 mb-1">Selection Summary:</div>
                    <div className="space-y-1 text-green-700">
                      <div>{selectedLicenseCodes.length} license code(s) selected</div>
                      <div>New codes cost: ₱{totalNewCost.toLocaleString()}</div>
                      <div className="font-medium border-t border-green-300 pt-1">
                        Grand Total: ₱{grandTotal.toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowLicenseAssignDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={assigningLicenseCode || selectedLicenseCodes.length === 0}>
                {assigningLicenseCode ? "Assigning..." : `Assign ${selectedLicenseCodes.length} Code(s)`}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* Course Completion Dialog */}
      <UIDialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Mark Course as Complete for {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          <div className="space-y-4">
            {selectedStudent && (() => {
              const completableEnrollments = getStudentEnrollments(selectedStudent.id!).filter(e => {
                if (e.status !== "approved") return false;
                const progress = getEnrollmentProgress(e);
                return progress.percentage >= 100;
              });
              
              if (completableEnrollments.length === 0) {
                return (
                  <div className="text-center py-4 text-muted-foreground">
                    No courses ready for completion found.
                  </div>
                );
              }
              
              return (
                <>
                  <div>
                    <Label htmlFor="completionEnrollmentId" className="text-sm font-medium">
                      Select Course to Mark as Complete *
                      {completableEnrollments.length > 1 && (
                        <span className="ml-1 text-green-600 font-normal">
                          ({completableEnrollments.length} courses ready)
                        </span>
                      )}
                    </Label>
                    <select
                      id="completionEnrollmentId"
                      value={selectedEnrollment?.id || ""}
                      onChange={(e) => {
                        const enrollment = completableEnrollments.find(en => en.id === e.target.value);
                        setSelectedEnrollment(enrollment || null);
                      }}
                      className="w-full border rounded p-2"
                      required
                    >
                      <option value="">
                        {completableEnrollments.length === 1 
                          ? "Select the course to complete" 
                          : `Choose from ${completableEnrollments.length} completed courses`
                        }
                      </option>
                      {completableEnrollments.map((enrollment, index) => {
                        const course = courses.find(c => c.id === enrollment.courseId);
                        const progress = getEnrollmentProgress(enrollment);
                        
                        return (
                          <option key={enrollment.id} value={enrollment.id}>
                            {index + 1}. {course?.title || 'Unknown Course'} - {progress.percentage}% Complete
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Show selected course details */}
                  {selectedEnrollment && (() => {
                    const course = courses.find(c => c.id === selectedEnrollment.courseId);
                    const progress = getEnrollmentProgress(selectedEnrollment);
                    const completedSessions = schedules.filter(s => 
                      s.enrollmentId === selectedEnrollment.id && s.status === "completed"
                    );
                    
                    return (
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <div className="text-sm font-medium text-green-800 mb-2">
                          ✅ Course Ready for Completion
                        </div>
                        <div className="text-sm space-y-1">
                          <div><strong>Course:</strong> {course?.title || 'Unknown'}</div>
                          <div><strong>Progress:</strong> {progress.completed}h / {progress.total}h ({progress.percentage}%)</div>
                          <div><strong>Completed Sessions:</strong> {completedSessions.length}</div>
                          <div className="text-green-700 font-medium mt-2">
                            ✓ This course has reached 100% completion and is ready to be marked as complete.
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div className="flex gap-2 justify-end">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowCompletionDialog(false);
                        setSelectedEnrollment(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => {
                        if (selectedEnrollment) {
                          handleMarkEnrollmentComplete(selectedEnrollment.id);
                        }
                      }}
                      disabled={completingEnrollment || !selectedEnrollment}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {completingEnrollment ? "Marking Complete..." : "Mark as Complete"}
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </UIDialogContent>
      </UIDialog>

      {/* Quick Complete Dialog */}
      <UIDialog open={showQuickCompleteDialog} onOpenChange={setShowQuickCompleteDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>Mark Course as Complete</UIDialogTitle>
          </UIDialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a course to mark as complete for {selectedStudent?.firstName} {selectedStudent?.lastName}. 
              This is useful for courses that don't require scheduling or have been completed outside the system.
            </p>
            
            <div>
              <Label htmlFor="courseSelection">Select Course to Complete *</Label>
              <select
                id="courseSelection"
                value={selectedCourseForCompletion}
                onChange={(e) => setSelectedCourseForCompletion(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Choose a course...</option>
                {selectedStudent && enrollments
                  .filter(e => 
                    e.studentId === selectedStudent.id && 
                    (e.status === "approved" || e.status === "partial_payment") &&
                    e.branch === userBranch
                  )
                  .map(enrollment => {
                    const course = courses.find(c => c.id === enrollment.courseId);
                    return (
                      <option key={enrollment.id} value={enrollment.id}>
                        {course?.title || 'Unknown Course'} - ₱{enrollment.courseFee?.toLocaleString()}
                        {enrollment.status === "partial_payment" && ` (Balance: ₱${enrollment.balance?.toLocaleString()})`}
                      </option>
                    );
                  })}
              </select>
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowQuickCompleteDialog(false);
                  setSelectedCourseForCompletion("");
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleQuickComplete}
                disabled={!selectedCourseForCompletion || completingEnrollment}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {completingEnrollment ? "Completing..." : "Mark as Complete"}
              </Button>
            </div>
          </div>
        </UIDialogContent>
      </UIDialog>

      {/* Transfer Student Dialog */}
      <UIDialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <UIDialogContent aria-describedby="transfer-dialog-description">
          <UIDialogHeader>
            <UIDialogTitle>Transfer Student</UIDialogTitle>
          </UIDialogHeader>
          <div id="transfer-dialog-description" className="sr-only">
            Transfer a student to a different branch. This will move all their enrollments and schedules to the new branch.
          </div>
          <div className="space-y-4">
            {selectedStudent && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium">Student to Transfer:</h4>
                <p className="text-sm text-gray-600">
                  {selectedStudent.firstName} {selectedStudent.middleName ? `${selectedStudent.middleName} ` : ''}{selectedStudent.lastName}
                </p>
                <p className="text-sm text-gray-500">
                  Current Branch: {branches.find(b => b.id === selectedStudent.branch)?.name || selectedStudent.branch}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="transferBranch">Select New Branch</Label>
              <Select
                value={transferData.newBranch}
                onValueChange={(value) => setTransferData({ ...transferData, newBranch: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches
                    .filter(branch => branch.id !== selectedStudent?.branch) // Exclude current branch
                    .map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowTransferDialog(false);
                  setTransferData({ newBranch: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedStudent && transferData.newBranch) {
                    handleTransferStudent(selectedStudent.id!, transferData.newBranch);
                  }
                }}
                disabled={!transferData.newBranch}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Transfer Student
              </Button>
            </div>
          </div>
        </UIDialogContent>
      </UIDialog>

      {/* Student Details Dialog */}
      <UIDialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <UIDialogContent className="max-w-2xl">
          <UIDialogHeader>
            <UIDialogTitle>Student Details</UIDialogTitle>
          </UIDialogHeader>
          {selectedStudent && (
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-lg mb-3">Personal Information</h3>
                  <div className="space-y-2">
                    <div><strong>Full Name:</strong> {selectedStudent.firstName} {selectedStudent.middleName ? `${selectedStudent.middleName} ` : ''}{selectedStudent.lastName}</div>
                    <div><strong>Phone:</strong> {selectedStudent.phone}</div>
                    {selectedStudent.email && <div><strong>Email:</strong> {selectedStudent.email}</div>}
                    {selectedStudent.address && <div><strong>Address:</strong> {selectedStudent.address}</div>}
                    {selectedStudent.dateOfBirth && <div><strong>Date of Birth:</strong> {selectedStudent.dateOfBirth}</div>}
                    {selectedStudent.gender && <div><strong>Gender:</strong> {selectedStudent.gender}</div>}
                    {selectedStudent.maritalStatus && <div><strong>Marital Status:</strong> {selectedStudent.maritalStatus}</div>}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-lg mb-3">Additional Information</h3>
                  <div className="space-y-2">
                    <div><strong>Registration Type:</strong> {getStudentRegistrationType(selectedStudent.id!) || 'N/A'}</div>
                    <div><strong>Branch:</strong> {(() => {
                      const branch = branches.find(b => b.id === selectedStudent.branch);
                      return branch ? branch.name : selectedStudent.branch || 'N/A';
                    })()}</div>
                    {selectedStudent.ltoClientNumber && <div><strong>LTO Client Number:</strong> {selectedStudent.ltoClientNumber}</div>}
                    {selectedStudent.licenseNumber && <div><strong>License Number:</strong> {selectedStudent.licenseNumber}</div>}
                    <div><strong>Role:</strong> {selectedStudent.role}</div>
                    <div><strong>Status:</strong> {getStudentStatusBadgeProps(selectedStudent).text}</div>
                  </div>
                </div>
              </div>

              {/* Enrollment Information */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Enrollment Information</h3>
                {(() => {
                  const studentEnrollments = getStudentEnrollments(selectedStudent.id!);
                  if (studentEnrollments.length === 0) {
                    return <p className="text-muted-foreground">No enrollments found.</p>;
                  }
                  
                  return (
                    <div className="space-y-3">
                      {studentEnrollments.map(enrollment => {
                        const course = courses.find(c => c.id === enrollment.courseId);
                        const progress = getEnrollmentProgress(enrollment);
                        
                        return (
                          <div key={enrollment.id} className="border rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-medium">{course?.title || 'Unknown Course'}</h4>
                                <p className="text-sm text-muted-foreground">
                                  Enrolled: {enrollment.enrolledAt ? format(enrollment.enrolledAt.toDate(), 'MMM dd, yyyy') : 'N/A'}
                                </p>
                              </div>
                              <Badge 
                                variant="secondary" 
                                className={
                                  enrollment.status === "approved" ? "!bg-green-100 !text-green-700" :
                                  enrollment.status === "partial_payment" ? "!bg-yellow-100 !text-yellow-700" :
                                  enrollment.status === "completed" ? "!bg-blue-100 !text-blue-700" :
                                  enrollment.status === "rejected" ? "!bg-red-100 !text-red-700" :
                                  "!bg-gray-100 !text-gray-700"
                                }
                              >
                                {enrollment.status === "partial_payment" ? "Partial Payment" : 
                                 enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p><strong>Course Fee:</strong> ₱{enrollment.courseFee?.toLocaleString()}</p>
                                <p><strong>Amount Paid:</strong> ₱{enrollment.amountReceived?.toLocaleString()}</p>
                                <p><strong>Balance:</strong> ₱{enrollment.balance?.toLocaleString()}</p>
                              </div>
                              <div>
                                <p><strong>Payment Method:</strong> {enrollment.paymentMethod}</p>
                                {/* Show OR numbers - combine legacy single OR and new multiple OR arrays */}
                                {(() => {
                                  const allORNumbers = [];
                                  
                                  // Add legacy OR number if exists
                                  if (enrollment.orNumber) {
                                    allORNumbers.push(enrollment.orNumber);
                                  }
                                  
                                  // Add new OR numbers if exists
                                  if (enrollment.orNumbers && enrollment.orNumbers.length > 0) {
                                    allORNumbers.push(...enrollment.orNumbers);
                                  }
                                  
                                  if (allORNumbers.length === 0) return null;
                                  
                                  return (
                                    <div>
                                      <p><strong>OR Numbers:</strong></p>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {allORNumbers.map((orNum, index) => (
                                          <span key={index} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                            {orNum}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                                {enrollment.status === "approved" && progress.total > 0 && (
                                  <p><strong>Progress:</strong> {progress.completed}h / {progress.total}h ({progress.percentage}%)</p>
                                )}
                                {enrollment.licenseCode && (
                                  <p><strong>License Code:</strong> {enrollment.licenseCode}</p>
                                )}
                              </div>
                            </div>
                            
                            {enrollment.notes && (
                              <div className="mt-2 p-2 bg-gray-50 rounded">
                                <p className="text-sm"><strong>Notes:</strong> {enrollment.notes}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Session History */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Session History</h3>
                {(() => {
                  const studentSchedules = getStudentSchedules(selectedStudent.id!);
                  if (studentSchedules.length === 0) {
                    return <p className="text-muted-foreground">No sessions scheduled.</p>;
                  }
                  
                  return (
                    <div className="space-y-2">
                      {studentSchedules.slice(0, 5).map(schedule => {
                        const course = courses.find(c => c.id === schedule.courseId);
                        const instructor = instructors.find(i => i.id === schedule.instructorId);
                        
                        return (
                          <div key={schedule.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                            <div>
                              <p className="font-medium">{course?.title || 'Unknown Course'}</p>
                              <p className="text-sm text-muted-foreground">
                                {schedule.date} at {schedule.time}
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge 
                                variant="secondary" 
                                className={
                                  schedule.status === "completed" ? "!bg-green-100 !text-green-700" :
                                  schedule.status === "scheduled" ? "!bg-blue-100 !text-blue-700" :
                                  schedule.status === "cancelled" ? "!bg-red-100 !text-red-700" :
                                  "!bg-gray-100 !text-gray-700"
                                }
                              >
                                {schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1)}
                              </Badge>
                              <p className="text-sm text-muted-foreground mt-1">
                                {instructor ? `${instructor.firstName} ${instructor.lastName}` : 'Unknown Instructor'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      {studentSchedules.length > 5 && (
                        <p className="text-sm text-muted-foreground text-center">
                          And {studentSchedules.length - 5} more sessions...
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </div>
        </UIDialogContent>
      </UIDialog>

      {/* Edit Student Dialog */}
      <UIDialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>Edit Student Information</UIDialogTitle>
          </UIDialogHeader>
          {selectedStudent && (
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateStudent();
            }} className="space-y-4">
              <div>
                <Label htmlFor="editStudentName">Student Name</Label>
                <Input
                  id="editStudentName"
                  value={`${selectedStudent.firstName} ${selectedStudent.middleName ? selectedStudent.middleName + ' ' : ''}${selectedStudent.lastName}`}
                  disabled
                  className="bg-gray-50"
                />
              </div>
              
              <div>
                <Label htmlFor="editLtoClientNumber">LTO Client Number</Label>
                <Input
                  id="editLtoClientNumber"
                  value={editFormData.ltoClientNumber}
                  onChange={(e) => setEditFormData({ ...editFormData, ltoClientNumber: e.target.value })}
                  placeholder="Enter LTO Client Number"
                />
              </div>
              
              <div>
                <Label htmlFor="editLicenseNumber">License Number</Label>
                <Input
                  id="editLicenseNumber"
                  value={editFormData.licenseNumber}
                  onChange={(e) => setEditFormData({ ...editFormData, licenseNumber: e.target.value })}
                  placeholder="Enter License Number"
                />
              </div>
              
              <div>
                <Label htmlFor="editMobileNumber">Mobile Number</Label>
                <Input
                  id="editMobileNumber"
                  value={editFormData.mobileNumber}
                  onChange={(e) => setEditFormData({ ...editFormData, mobileNumber: e.target.value })}
                  placeholder="Enter Mobile Number"
                />
              </div>
              
              <div>
                <Label htmlFor="editAddress">Address</Label>
                <Input
                  id="editAddress"
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  placeholder="Enter Address"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Update Student
                </Button>
              </div>
            </form>
          )}
        </UIDialogContent>
      </UIDialog>

      {/* Message Dialog */}
      <MessageDialog
        open={message.open}
        onClose={() => setMessage({ ...message, open: false })}
        title={message.type === "error" ? "Error" : message.type === "success" ? "Success" : "Information"}
        message={message.text}
        type={message.type}
      />
    </div>
  );
};

export default StaffEnrollmentManagement;